import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import BottomNav from "../components/BottomNav";
import EmployeeSearch from "../components/EmployeeSearch";
import { useAdminUnlock } from "../components/GlobalViewAs";
import { api, ApiError } from "../api/client";
import { useAuth } from "../context/AuthContext";
import PeerStatusChips, { type PeerStatus } from "../components/PeerStatusChips";
import ReviewStagePips from "../components/ReviewStagePips";
import { reviewStatusLabel, reviewStatusNext, reviewStatusTone } from "../utils/reviewStatus";
import { CheckCircle2, Check, Clock, Eye, FileText, Save } from "lucide-react";
import AppraisalJourney from "../components/appraisal/AppraisalJourney";
import RatingScale from "../components/appraisal/RatingScale";
import JobDescriptionPanel, { hasJobDescription, type JobDescriptionInfo } from "../components/appraisal/JobDescriptionPanel";
import { JdAreaHint, JdCompetencyHint, JdObjectiveIdeas, findCompetency, findKra, onlineJd } from "../components/appraisal/JdLink";
import ExampleBox from "../components/appraisal/ExampleBox";
import TimeframePicker from "../components/appraisal/TimeframePicker";
import CheckInPicker from "../components/appraisal/CheckInPicker";
import MeetingPanel, { MyMeetingCard, isMeetingStage, type MeetingInfo } from "../components/appraisal/MeetingPanel";
import MeetingGuide from "../components/appraisal/MeetingGuide";
import { ObjectivesHighlight } from "../components/appraisal/Objectives";
import {
  appraisalName, appraisalNow, dueText, formatDate, missingExample, needsExample,
  selfAssessmentSteps, selfRatingText,
} from "../utils/appraisal";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ReviewSummary {
  id: number;
  cycle_id: number;
  cycle_name: string;
  review_period_start: string | null;
  review_period_end: string | null;
  employee_id: number;
  employee_name: string;
  manager_id: number | null;
  manager_name: string | null;
  status: string;
  overall_self_rating: number | null;
  overall_manager_rating: number | null;
  overall_agreed_rating: number | null;
  recommendation: string | null;
  self_assessment_deadline: string | null;
  colleague_feedback_deadline: string | null;
  manager_review_deadline: string | null;
  is_historical: boolean;
  historical_summary: string | null;
  review_date: string | null;
  document_count: number;
  /** The year this is the appraisal for ("2026 appraisal"). */
  appraisal_year?: number | null;
  /** Self and colleague ratings are words (1–4), not the company 1–5 scale. */
  rating_words?: boolean;
  /** The booked review meeting, once there is one. */
  meeting?: MeetingInfo | null;
}

interface ReviewFull extends ReviewSummary {
  feedback_released_at?: string | null;
  top_priority_1: string | null;
  top_priority_2: string | null;
  employee_response: string | null;
  calibration_summary: string | null;
  feedback_synthesis: string | null;
  additional_comments: string | null;
  role_aspirations: string | null;
  could_do_better: string | null;
  check_in_cadence: string | null;
  check_in_minutes?: number | null;
  check_in_time?: string | null;
  overall_example?: string | null;
  meeting_notes?: string | null;
  risk_flight_risk: string | null;
  risk_retention_priority: string | null;
  risk_successor: string | null;
  risk_single_point_of_failure: boolean | null;
  risk_commentary: string | null;
  signed_by_employee: boolean;
  signed_by_manager: boolean;
  signed_by_hr: boolean;
  meeting_date: string | null;
  responsibilities: ResponsibilityRow[];
  strengths: StrengthRow[];
  development_areas: DevAreaRow[];
  behaviours: BehaviourRow[];
  objectives: ObjectiveRow[];
  development_plan: DevPlanRow[];
  action_recommendations: ActionRecRow[];
  feedback_themes: FeedbackThemeRow[];
  sections: SectionRow[];
  documents: { id: number; document_id: number; label: string | null; file_name?: string }[];
}

interface ResponsibilityRow {
  id?: number;
  responsibility_area: string;
  employee_summary: string;
  self_rating: number | null;
  manager_evidence?: string;
  manager_rating?: number | null;
  /** The example a 4 or 5 needs. */
  manager_example?: string;
}

interface StrengthRow {
  id?: number;
  source?: string;
  strength: string;
  still_valid_example: string;
  evidence_impact?: string;
}

interface DevAreaRow {
  id?: number;
  source?: string;
  area: string;
  employee_progress: string;
  self_rating: number | null;
  manager_current_state?: string;
  manager_what_good_looks_like?: string;
}

interface BehaviourRow {
  id?: number;
  behaviour_name: string;
  employee_example: string;
  employee_rating: number | null;
  manager_evidence?: string;
  manager_rating?: number | null;
  /** The example a 4 or 5 needs. */
  manager_example?: string;
}

interface ObjectiveRow {
  id?: number;
  source?: string;
  objective: string;
  how_measured?: string;
  due_date?: string;
  /** "1m" | "3m" | "6m" | "12m": counted from the review meeting. */
  timeframe?: string;
}

interface DevPlanRow {
  id?: number;
  support_needed: string;
  provided_by?: string;
  target_date?: string;
  timeframe?: string;
}

interface ActionRecRow {
  id?: number;
  action_type: string;
  recommended: boolean;
  commentary?: string;
}

interface FeedbackThemeRow {
  id?: number;
  theme: string;
  reviewers_raising_it?: string;
  manager_view?: string;
}

interface SectionRow {
  section_number: number;
  section_type: string;
  is_complete: boolean;
}

interface ColleagueFeedbackItem {
  id: number;
  review_id: number;
  employee_name: string;
  focus_area: string;
  status: string;
  deadline: string | null;
  responses: FeedbackResponse[];
  appraisal_year?: number | null;
  rating_words?: boolean;
}

interface FeedbackResponse {
  id: number;
  question: string;
  answer: string | null;
  rating: number | null;
  sort_order: number;
}

/** A peer reviewer as seen by the manager in the manager-review wizard. */
interface ColleagueSummary {
  id: number;
  review_id: number;
  colleague_id: number;
  colleague_name: string;
  focus_area: string | null;
  status: string;
  deadline: string | null;
  submitted_at: string | null;
  responses: FeedbackResponse[];
}

// ---------------------------------------------------------------------------
// Self-assessment form state
// ---------------------------------------------------------------------------

interface SelfAssessmentState {
  responsibilities: ResponsibilityRow[];
  strengths: StrengthRow[];
  development_areas: DevAreaRow[];
  could_do_better: string;
  behaviours: BehaviourRow[];
  objectives: ObjectiveRow[];
  additional_comments: string;
  role_aspirations: string;
  overall_self_rating: number | null;
  top_priority_1: string;
  top_priority_2: string;
}

// ---------------------------------------------------------------------------
// Manager review form state
// ---------------------------------------------------------------------------

interface ManagerReviewState {
  responsibilities: ResponsibilityRow[];
  strengths: { strength: string; evidence_impact: string }[];
  development_areas: { area: string; manager_current_state: string; manager_what_good_looks_like: string }[];
  behaviours: BehaviourRow[];
  feedback_themes: FeedbackThemeRow[];
  objectives: { objective: string; how_measured: string; timeframe: string }[];
  development_plan: DevPlanRow[];
  overall_manager_rating: number | null;
  /** Why the overall rating is a 4 or 5. */
  overall_example: string;
  calibration_summary: string;
  feedback_synthesis: string;
  risk_flight_risk: string;
  risk_retention_priority: string;
  risk_successor: string;
  risk_single_point_of_failure: boolean;
  risk_commentary: string;
  check_in_cadence: string;
  check_in_minutes: number;
  check_in_time: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  not_started: { label: "Not Started", color: "bg-gray-100 text-gray-700" },
  self_assessment_in_progress: { label: "Self-Assessment In Progress", color: "bg-blue-100 text-blue-700" },
  self_assessment_submitted: { label: "Self-Assessment Submitted", color: "bg-yellow-100 text-yellow-700" },
  colleague_feedback_in_progress: { label: "Colleague Feedback In Progress", color: "bg-orange-100 text-orange-700" },
  colleague_feedback_complete: { label: "Colleague Feedback Complete", color: "bg-orange-100 text-orange-700" },
  manager_review_in_progress: { label: "Manager Review In Progress", color: "bg-purple-100 text-purple-700" },
  manager_review_submitted: { label: "Manager Review Submitted", color: "bg-purple-100 text-purple-700" },
  meeting_pending: { label: "Meeting Pending", color: "bg-pink-100 text-pink-700" },
  employee_response_pending: { label: "Your Response Needed", color: "bg-amber-100 text-amber-700" },
  completed: { label: "Completed", color: "bg-green-100 text-green-700" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700" },
};

const DEFAULT_BEHAVIOURS: string[] = [
  "Communication",
  "Teamwork",
  "Problem Solving",
  "Adaptability",
  "Leadership",
  "Accountability",
  "Customer Focus",
];

const TOTAL_STEPS = 9;
const MANAGER_TOTAL_STEPS = 12;

const MANAGER_STEP_LABELS = [
  "Summary of the year",
  "Responsibilities",
  "Strengths",
  "Development",
  "Colleague feedback",
  "Behaviours",
  "Objectives",
  "Development plan",
  "Your rating",
  "Catch-ups",
  "Risk",
  "Check and submit",
];

// en-GB style everywhere ("4 Nov 2026"); `format(new Date(iso))` keeps the
// existing call sites working.
const DATE_FMT = {
  format: (d: Date) => (Number.isNaN(d.getTime()) ? "" : formatDate(d.toISOString())),
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function RatingInput({ value, onChange, disabled, scale = "company" }: { value: number | null; onChange: (v: number) => void; disabled?: boolean; scale?: "company" | "self" | "colleague" }) {
  return <RatingScale value={value} onChange={onChange} disabled={disabled} scale={scale} />;
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${reviewStatusTone(status)}`}>
      {reviewStatusLabel(status)}
    </span>
  );
}

function ProgressBar({ step, completed }: { step: number; completed: number }) {
  const steps = selfAssessmentSteps();
  const pct = Math.round((completed / TOTAL_STEPS) * 100);
  return (
    <div className="mb-3">
      <div className="mb-1 flex items-center justify-between text-xs text-gray-500">
        <span>
          Step {step} of {TOTAL_STEPS} · <span className="font-semibold text-pme-navy">{steps[step - 1]?.title}</span>
        </span>
        <span>{completed} of {TOTAL_STEPS} done</span>
      </div>
      <div className="h-2 rounded-full bg-gray-200">
        <div className="h-2 rounded-full bg-pme-blue transition-all" style={{ width: `${pct}%` }} />
      </div>
      {step < TOTAL_STEPS && (
        <p className="mt-1.5 text-xs text-gray-500">
          <span className="font-semibold text-gray-600">Coming up:</span>{" "}
          {steps.slice(step).map((s) => s.title).join(" · ")}
        </p>
      )}
    </div>
  );
}

function StepNav({ step, onStep, completedSteps }: { step: number; onStep: (s: number) => void; completedSteps: Set<number> }) {
  const steps = selfAssessmentSteps();
  const rowRef = useRef<HTMLDivElement>(null);
  // Keep the current step's chip in view on a phone.
  useEffect(() => {
    const el = rowRef.current?.querySelector<HTMLElement>('[aria-current="step"]');
    el?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [step]);
  return (
    <div ref={rowRef} className="-mx-1 mb-4 flex gap-1.5 overflow-x-auto px-1 pb-1 sm:flex-wrap sm:overflow-visible">
      {steps.map(({ n, short }) => {
        const isCurrent = n === step;
        const isDone = completedSteps.has(n);
        return (
          <button
            key={n}
            type="button"
            onClick={() => onStep(n)}
            aria-current={isCurrent ? "step" : undefined}
            className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${
              isCurrent
                ? "border-pme-blue bg-pme-blue text-white"
                : isDone
                  ? "border-green-200 bg-green-50 text-green-700 hover:bg-green-100"
                  : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
            }`}
          >
            {isDone && !isCurrent ? <Check size={12} strokeWidth={3} aria-hidden /> : <span>{n}</span>}
            {short}
          </button>
        );
      })}
    </div>
  );
}

/** Title + plain guidance for one self-assessment step. */
function StepGuide({ n }: { n: number }) {
  const step = selfAssessmentSteps()[n - 1];
  if (!step) return null;
  return (
    <div className="mb-3">
      <p className="text-sm text-gray-600">{step.intro}</p>
      {step.tips.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 text-xs text-gray-500">
          {step.tips.map((tip) => (
            <li key={tip} className="flex gap-1.5"><span aria-hidden className="text-pme-blue">•</span>{tip}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ManagerStepNav({ step, onStep, completedSteps }: { step: number; onStep: (s: number) => void; completedSteps: Set<number> }) {
  return (
    <div className="flex gap-1 flex-wrap mb-4">
      {MANAGER_STEP_LABELS.map((label, i) => {
        const s = i + 1;
        const isCurrent = s === step;
        const isDone = completedSteps.has(s);
        const canNav = isDone || s <= step;
        return (
          <button
            key={s}
            type="button"
            disabled={!canNav}
            onClick={() => onStep(s)}
            title={label}
            className={`inline-flex h-7 items-center gap-1 rounded-full px-2.5 text-xs font-semibold transition-colors ${
              isCurrent
                ? "bg-purple-600 text-white"
                : isDone
                  ? "bg-green-100 text-green-700 hover:bg-green-200"
                  : canNav
                    ? "bg-gray-200 text-gray-600 hover:bg-gray-300"
                    : "bg-gray-100 text-gray-300 cursor-not-allowed"
            }`}
          >
            {isDone && !isCurrent ? <Check size={13} strokeWidth={2.5} aria-label="Done" /> : <span>{s}</span>}
            <span className={isCurrent ? "" : "hidden sm:inline"}>{label}</span>
          </button>
        );
      })}
    </div>
  );
}

function ManagerProgressBar({ step }: { step: number }) {
  const pct = Math.round((step / MANAGER_TOTAL_STEPS) * 100);
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
        <span>Step {step} of {MANAGER_TOTAL_STEPS} — {MANAGER_STEP_LABELS[step - 1]}</span>
        <span>{pct}%</span>
      </div>
      <div className="h-2 rounded-full bg-gray-200">
        <div
          className="h-2 rounded-full bg-purple-600 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

type RatedItem = { manager_rating?: number | null; manager_example?: string };

function hasBackedHighRating(form: { responsibilities: RatedItem[]; behaviours: RatedItem[] }): boolean {
  return [...form.responsibilities, ...form.behaviours].some(
    (r) => needsExample(r.manager_rating) && !missingExample(r.manager_rating, r.manager_example),
  );
}

/** What stops a manager review moving on, step by step. The server applies
 *  the same rule when the review is submitted. */
function managerExampleGaps(
  form: {
    responsibilities: (RatedItem & { responsibility_area: string })[];
    behaviours: (RatedItem & { behaviour_name: string })[];
    overall_manager_rating: number | null;
    overall_example: string;
  },
  step?: number,
): string[] {
  const gaps: string[] = [];
  if (step == null || step === 2) {
    form.responsibilities.forEach((r) => {
      if (missingExample(r.manager_rating, r.manager_example)) gaps.push(r.responsibility_area || "a responsibility");
    });
  }
  if (step == null || step === 6) {
    form.behaviours.forEach((b) => {
      if (missingExample(b.manager_rating, b.manager_example)) gaps.push(b.behaviour_name || "a behaviour");
    });
  }
  if ((step == null || step === 9) && needsExample(form.overall_manager_rating)) {
    if (missingExample(form.overall_manager_rating, form.overall_example)) gaps.push("why the overall rating is a " + form.overall_manager_rating);
    if (!hasBackedHighRating(form)) gaps.push("at least one 4 or 5 with an example (steps 2 and 6)");
  }
  return gaps;
}

/** How to use the company scale, shown to the manager wherever they rate. */
function ManagerRatingGuide({ compact = false }: { compact?: boolean }) {
  return (
    <div className="mb-4 rounded-xl border border-purple-200 bg-purple-50 p-3 text-sm text-purple-900">
      <p className="font-semibold">How to rate</p>
      <p className="mt-0.5 text-purple-800">
        <span className="font-semibold">3 means a solid job</span>: they do what the role needs, well and consistently. Most good performers sit here.
      </p>
      {!compact && (
        <ul className="mt-1.5 space-y-0.5 text-xs text-purple-800">
          <li><span className="font-semibold">4</span> means they went above what the role needs. A box opens for you to say how.</li>
          <li><span className="font-semibold">5</span> means outstanding. A box opens for clear examples.</li>
          <li>"Great attitude" or "always helpful" is not an example. "Ran the October payroll alone while Sam was off, with no errors" is.</li>
          <li><span className="font-semibold">1 or 2</span>: say what needs to change and what support you'll give.</li>
        </ul>
      )}
    </div>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="mb-2">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="text-sm text-gray-700 whitespace-pre-wrap">{value}</p>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function ReviewPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hasPermission, isAdmin, employee: currentUser } = useAuth();
  const { adminUnlocked } = useAdminUnlock();

  // Modes derived from URL params
  const selectedReviewId = searchParams.get("review") ? Number(searchParams.get("review")) : null;
  const activeTab = searchParams.get("tab") || "";
  const currentStep = searchParams.get("step") ? Number(searchParams.get("step")) : 1;
  const colleagueFeedbackId = searchParams.get("feedback") ? Number(searchParams.get("feedback")) : null;

  // --- List tab ---
  const showTeamTab = hasPermission("can_view_team") || hasPermission("can_approve_leave");
  const [listTab, setListTab] = useState<"my-reviews" | "team-reviews">(
    searchParams.get("list") === "team-reviews" && showTeamTab ? "team-reviews" : "my-reviews"
  );

  // --- List mode state ---
  const [reviews, setReviews] = useState<ReviewSummary[]>([]);
  const [teamReviews, setTeamReviews] = useState<ReviewSummary[]>([]);
  const [colleagueFeedbacks, setColleagueFeedbacks] = useState<{ id: number; review_id: number; employee_name: string; focus_area: string; status: string; deadline: string | null }[]>([]);
  const [listLoading, setListLoading] = useState(true);
  // Self-assessment helpers: the "before you start" note, and the thank-you
  // shown straight after submitting.
  const [introDismissed, setIntroDismissed] = useState(false);
  // The job description, shown beside the self-assessment.
  const [jobDescription, setJobDescription] = useState<JobDescriptionInfo | null>(null);
  const [jdOpen, setJdOpen] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [listError, setListError] = useState("");
  const [colleagueModalReview, setColleagueModalReview] = useState<ReviewSummary | null>(null);

  // --- Review mode state ---
  const [reviewData, setReviewData] = useState<ReviewFull | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState("");

  // Peer feedback status on the detail landing — manager/admin only, never
  // loaded for the review's own subject.
  const [landingPeers, setLandingPeers] = useState<PeerStatus[] | null>(null);
  const [landingPeersError, setLandingPeersError] = useState("");

  useEffect(() => {
    setLandingPeers(null);
    setLandingPeersError("");
    if (!reviewData || !selectedReviewId || activeTab) return;
    const own = reviewData.employee_id === currentUser?.employee_id;
    const assigned = reviewData.manager_id != null && reviewData.manager_id === currentUser?.employee_id;
    if (own || !(assigned || isAdmin)) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await api.get<{ colleagues: { id: number; colleague_name: string; status: string; deadline: string | null; invited_at?: string | null }[] }>(
          `/api/reviews/${selectedReviewId}/colleagues`,
        );
        if (cancelled) return;
        const today = new Date().toISOString().slice(0, 10);
        setLandingPeers(
          (data?.colleagues ?? []).map((c) => ({
            id: c.id,
            name: c.colleague_name,
            status: c.status,
            overdue: c.status !== "submitted" && !!c.deadline && c.deadline < today,
            deadline: c.deadline,
            invited: c.invited_at != null,
          })),
        );
      } catch {
        if (!cancelled) setLandingPeersError("Couldn't load colleague feedback status.");
      }
    })();
    return () => { cancelled = true; };
  }, [reviewData, selectedReviewId, activeTab, currentUser?.employee_id, isAdmin]);

  useEffect(() => {
    setJobDescription(null);
    setJdOpen(false);
    if (!selectedReviewId || !["self-assessment", "manager-review", "meeting"].includes(activeTab)) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await api.get<JobDescriptionInfo>(`/api/reviews/${selectedReviewId}/job-description`);
        if (!cancelled) setJobDescription(data);
      } catch { /* no JD to show */ }
    })();
    return () => { cancelled = true; };
  }, [selectedReviewId, activeTab]);

  // --- Self-assessment state ---
  const [formState, setFormState] = useState<SelfAssessmentState>({
    responsibilities: [],
    strengths: [],
    development_areas: [],
    could_do_better: "",
    behaviours: [],
    objectives: [],
    additional_comments: "",
    role_aspirations: "",
    overall_self_rating: null,
    top_priority_1: "",
    top_priority_2: "",
  });

  // A fresh self-assessment starts from the job description kept in PME
  // People: its key result areas become the responsibility rows and its
  // competencies the behaviours. Anything already saved is left alone.
  const seededFor = useRef<number | null>(null);
  useEffect(() => {
    const jd = jobDescription?.online?.content;
    if (!jd || !reviewData || seededFor.current === reviewData.id) return;
    if (reviewData.employee_id !== currentUser?.employee_id) return;
    seededFor.current = reviewData.id;
    setFormState((f) => {
      const next = { ...f };
      const blankRows = f.responsibilities.every((r) => !r.responsibility_area.trim() && !r.employee_summary.trim() && r.self_rating == null);
      const areas = jd.kras.map((k) => k.area.trim()).filter(Boolean);
      if (reviewData.responsibilities.length === 0 && blankRows && areas.length > 0) {
        next.responsibilities = areas.map((a) => ({ responsibility_area: a, employee_summary: "", self_rating: null }));
      }
      const untouched = f.behaviours.every((b) => !b.id && !b.employee_example.trim() && b.employee_rating == null);
      const names = jd.competencies.map((c) => c.name.trim()).filter(Boolean);
      if (reviewData.behaviours.length === 0 && untouched && names.length > 0) {
        next.behaviours = names.map((n) => ({ behaviour_name: n, employee_example: "", employee_rating: null }));
      }
      return next;
    });
  }, [jobDescription, reviewData, currentUser?.employee_id]);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  // --- Colleague feedback state ---
  const [feedbackData, setFeedbackData] = useState<ColleagueFeedbackItem | null>(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  // --- Employee response state ---
  const [employeeResponse, setEmployeeResponse] = useState("");
  const [responseSaving, setResponseSaving] = useState(false);
  const [responseSaved, setResponseSaved] = useState(false);

  // --- Manager review state ---
  const [mgrData, setMgrData] = useState<ReviewFull | null>(null);
  const [mgrLoading, setMgrLoading] = useState(false);
  const [mgrForm, setMgrForm] = useState<ManagerReviewState>({
    responsibilities: [],
    strengths: [],
    development_areas: [],
    behaviours: [],
    feedback_themes: [],
    objectives: [],
    development_plan: [],
    overall_manager_rating: null,
    overall_example: "",
    calibration_summary: "",
    feedback_synthesis: "",
    risk_flight_risk: "",
    risk_retention_priority: "",
    risk_successor: "",
    risk_single_point_of_failure: false,
    risk_commentary: "",
    check_in_cadence: "",
    check_in_minutes: 15,
    check_in_time: "09:30",
  });
  const [mgrCompletedSteps, setMgrCompletedSteps] = useState<Set<number>>(new Set());
  const [mgrSaving, setMgrSaving] = useState(false);
  const [mgrSaveMsg, setMgrSaveMsg] = useState("");
  const [mgrShowConfirm, setMgrShowConfirm] = useState(false);
  const [colleagueFeedbackList, setColleagueFeedbackList] = useState<ColleagueSummary[]>([]);
  // Section 4: super-admin "Preview as employee" — renders the subject's own
  // redacted payload through this same component.
  const [subjectPreview, setSubjectPreview] = useState(false);
  const [adminReviewData, setAdminReviewData] = useState<ReviewFull | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [releasing, setReleasing] = useState(false);
  const [releaseError, setReleaseError] = useState("");

  // --- Review documents ---
  const [reviewDocs, setReviewDocs] = useState<{ id: number; document_id: number; title: string; file_name: string; file_type?: string; file_size_bytes?: number; label?: string }[]>([]);
  const [docFiles, setDocFiles] = useState<File[]>([]);
  const [docUploading, setDocUploading] = useState(false);
  const docFileRef = useRef<HTMLInputElement>(null);

  // --- Confirm dialog ---
  const [showConfirm, setShowConfirm] = useState(false);

  // Completed steps tracking
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  // ---------- Navigation helpers ----------

  const fromEmployee = searchParams.get("from") === "employee";
  const fromEmployeeId = searchParams.get("eid");

  const goToList = useCallback(() => {
    if (fromEmployee && fromEmployeeId) {
      navigate(`/admin/employees/${fromEmployeeId}?tab=performance`);
      return;
    }
    // From a review or a feedback request, "back" goes to the Performance
    // page (the old list view at /review is not linked from anywhere).
    const ownReview = reviewData && currentUser && reviewData.employee_id === currentUser.employee_id;
    if (reviewData && !ownReview) {
      navigate("/performance?tab=team-reviews");
      return;
    }
    navigate("/performance");
  }, [fromEmployee, fromEmployeeId, navigate, reviewData, currentUser]);

  const goToReview = useCallback((id: number, tab?: string) => {
    const params: Record<string, string> = { review: String(id) };
    if (tab) params.tab = tab;
    if (tab === "self-assessment" || tab === "manager-review") params.step = "1";
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const goToStep = useCallback((s: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("step", String(s));
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const goToFeedback = useCallback((feedbackId: number) => {
    setSearchParams({ feedback: String(feedbackId) }, { replace: true });
  }, [setSearchParams]);

  // ---------- Load list ----------

  const loadList = useCallback(async () => {
    setListLoading(true);
    setListError("");
    try {
      const [myReviews, feedbacks] = await Promise.all([
        api.get<ReviewSummary[]>("/api/reviews"),
        api.get<{ id: number; review_id: number; employee_name: string; focus_area: string; status: string; deadline: string | null }[]>("/api/reviews/my-colleague-feedbacks").catch(() => []),
      ]);
      setReviews(myReviews);
      setColleagueFeedbacks(feedbacks);
      if (showTeamTab) {
        const team = await api.get<ReviewSummary[]>("/api/reviews?role_filter=manager");
        setTeamReviews(team.filter((r) => r.status !== "completed" && r.status !== "cancelled"));
      }
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : "Failed to load reviews");
    } finally {
      setListLoading(false);
    }
  }, [showTeamTab]);

  useEffect(() => {
    if (selectedReviewId || colleagueFeedbackId) return;
    void loadList();
  }, [selectedReviewId, colleagueFeedbackId, loadList]);

  // ---------- Load review detail ----------

  useEffect(() => {
    if (!selectedReviewId) return;
    let cancelled = false;
    setReviewLoading(true);
    setReviewError("");

    (async () => {
      try {
        const data = await api.get<ReviewFull>(`/api/reviews/${selectedReviewId}/self-assessment`);
        if (cancelled) return;
        setReviewData(data);

        // Populate form state from review data
        setFormState({
          responsibilities: data.responsibilities.length > 0
            ? data.responsibilities.map((r) => ({
                id: r.id,
                responsibility_area: r.responsibility_area || "",
                employee_summary: r.employee_summary || "",
                self_rating: r.self_rating ?? null,
              }))
            : [{ responsibility_area: "", employee_summary: "", self_rating: null }],
          strengths: data.strengths.length > 0
            ? data.strengths.map((s) => ({
                id: s.id,
                strength: s.strength || "",
                still_valid_example: s.still_valid_example || "",
              }))
            : [{ strength: "", still_valid_example: "" }],
          development_areas: data.development_areas.length > 0
            ? data.development_areas.map((d) => ({
                id: d.id,
                area: d.area || "",
                employee_progress: d.employee_progress || "",
                self_rating: d.self_rating ?? null,
              }))
            : [{ area: "", employee_progress: "", self_rating: null }],
          could_do_better: data.could_do_better || "",
          behaviours: data.behaviours.length > 0
            ? data.behaviours.map((b) => ({
                id: b.id,
                behaviour_name: b.behaviour_name || "",
                employee_example: b.employee_example || "",
                employee_rating: b.employee_rating ?? null,
              }))
            : DEFAULT_BEHAVIOURS.map((name) => ({
                behaviour_name: name,
                employee_example: "",
                employee_rating: null,
              })),
          objectives: data.objectives.length > 0
            ? data.objectives.map((o) => ({
                id: o.id,
                objective: o.objective || "",
              }))
            : [{ objective: "" }],
          additional_comments: data.additional_comments || "",
          role_aspirations: data.role_aspirations || "",
          overall_self_rating: data.overall_self_rating,
          top_priority_1: data.top_priority_1 || "",
          top_priority_2: data.top_priority_2 || "",
        });

        setEmployeeResponse(data.employee_response || "");
        setResponseSaved(!!data.employee_response);

        // Build completed steps from sections
        const done = new Set<number>();
        for (const sec of data.sections) {
          if (sec.section_type === "self_assessment" && sec.is_complete) {
            done.add(sec.section_number);
          }
        }
        setCompletedSteps(done);

        // Opened from "Continue my self-assessment" (no step in the link):
        // go to the first step not saved yet.
        const params = new URLSearchParams(window.location.search);
        if (params.get("tab") === "self-assessment" && !params.get("step") && done.size > 0) {
          let first = TOTAL_STEPS;
          for (let n = 1; n <= TOTAL_STEPS; n += 1) {
            if (!done.has(n)) { first = n; break; }
          }
          goToStep(first);
        }
      } catch (err) {
        if (!cancelled) {
          setReviewError(err instanceof ApiError ? err.message : "Failed to load review");
        }
      } finally {
        if (!cancelled) setReviewLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [selectedReviewId]);

  // ---------- Load colleague feedback ----------

  useEffect(() => {
    if (!colleagueFeedbackId) return;
    let cancelled = false;
    setFeedbackLoading(true);

    (async () => {
      try {
        const data = await api.get<ColleagueFeedbackItem>(`/api/reviews/colleague-feedback/${colleagueFeedbackId}`);
        if (!cancelled) setFeedbackData(data);
      } catch (err) {
        if (!cancelled) {
          setReviewError(err instanceof ApiError ? err.message : "Failed to load feedback");
        }
      } finally {
        if (!cancelled) setFeedbackLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [colleagueFeedbackId]);

  // ---------- Load manager review data ----------

  const mgrLoadedReviewId = useRef<number | null>(null);

  useEffect(() => {
    if (!selectedReviewId || activeTab !== "manager-review") return;
    if (mgrLoadedReviewId.current === selectedReviewId) return;
    mgrLoadedReviewId.current = selectedReviewId;
    let cancelled = false;
    let finished = false;
    setMgrLoading(true);

    (async () => {
      try {
        const data = await api.get<ReviewFull>(`/api/reviews/${selectedReviewId}/manager-review`);
        if (cancelled) return;
        setMgrData(data);

        const mgrStrengths = data.strengths.filter((s) => s.source === "manager");
        const mgrDevAreas = data.development_areas.filter((d) => d.source === "manager");
        const mgrObjectives = data.objectives.filter((o) => o.source === "manager_agreed");

        setMgrForm({
          responsibilities: data.responsibilities.map((r) => ({
            id: r.id,
            responsibility_area: r.responsibility_area,
            employee_summary: r.employee_summary,
            self_rating: r.self_rating,
            manager_evidence: r.manager_evidence || "",
            manager_rating: r.manager_rating ?? null,
            manager_example: r.manager_example || "",
          })),
          strengths: mgrStrengths.length > 0
            ? mgrStrengths.map((s) => ({ strength: s.strength, evidence_impact: s.evidence_impact || "" }))
            : [{ strength: "", evidence_impact: "" }],
          development_areas: mgrDevAreas.length > 0
            ? mgrDevAreas.map((d) => ({ area: d.area, manager_current_state: d.manager_current_state || "", manager_what_good_looks_like: d.manager_what_good_looks_like || "" }))
            : [{ area: "", manager_current_state: "", manager_what_good_looks_like: "" }],
          behaviours: data.behaviours.map((b) => ({
            id: b.id,
            behaviour_name: b.behaviour_name,
            employee_example: b.employee_example,
            employee_rating: b.employee_rating,
            manager_evidence: b.manager_evidence || "",
            manager_rating: b.manager_rating ?? null,
            manager_example: b.manager_example || "",
          })),
          feedback_themes: data.feedback_themes.length > 0
            ? data.feedback_themes.map((t) => ({ id: t.id, theme: t.theme, reviewers_raising_it: t.reviewers_raising_it || "", manager_view: t.manager_view || "" }))
            : [{ theme: "", reviewers_raising_it: "", manager_view: "" }],
          objectives: mgrObjectives.length > 0
            ? mgrObjectives.map((o) => ({ objective: o.objective, how_measured: o.how_measured || "", timeframe: o.timeframe || "" }))
            : [{ objective: "", how_measured: "", timeframe: "" }],
          development_plan: data.development_plan.length > 0
            ? data.development_plan.map((p) => ({ id: p.id, support_needed: p.support_needed, provided_by: p.provided_by || "", timeframe: p.timeframe || "" }))
            : [{ support_needed: "", provided_by: "", timeframe: "" }],
          overall_manager_rating: data.overall_manager_rating,
          overall_example: data.overall_example || "",
          calibration_summary: data.calibration_summary || "",
          feedback_synthesis: data.feedback_synthesis || "",
          risk_flight_risk: data.risk_flight_risk || "",
          risk_retention_priority: data.risk_retention_priority || "",
          risk_successor: data.risk_successor || "",
          risk_single_point_of_failure: data.risk_single_point_of_failure ?? false,
          risk_commentary: data.risk_commentary || "",
          check_in_cadence: data.check_in_cadence || "",
          check_in_minutes: data.check_in_minutes ?? 15,
          check_in_time: data.check_in_time || "09:30",
        });

        setEmployeeResponse(data.employee_response || "");
        setResponseSaved(!!data.employee_response);

        const done = new Set<number>();
        for (const sec of data.sections) {
          if (sec.section_type === "manager_review" && sec.is_complete) {
            done.add(sec.section_number);
          }
        }
        setMgrCompletedSteps(done);

        if (done.size > 0) {
          let highest = 0;
          done.forEach((s) => { if (s > highest) highest = s; });
          const nextStep = highest < MANAGER_TOTAL_STEPS ? highest + 1 : 1;
          goToStep(nextStep);
        }

        try {
          // NB: this endpoint returns { colleagues, focus_areas } — not a bare array.
          const colleagueResp = await api.get<{ colleagues: ColleagueSummary[]; focus_areas: string[] | null }>(`/api/reviews/${selectedReviewId}/colleagues`);
          if (!cancelled) setColleagueFeedbackList(colleagueResp?.colleagues ?? []);
        } catch {
          // non-critical
        }

        try {
          const docs = await api.get<typeof reviewDocs>(`/api/reviews/${selectedReviewId}/documents`);
          if (!cancelled) setReviewDocs(docs);
        } catch {
          // non-critical
        }
      } catch (err) {
        if (!cancelled) {
          setReviewError(err instanceof ApiError ? err.message : "Failed to load manager review");
        }
      } finally {
        finished = true;
        if (!cancelled) setMgrLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      // Cut off before it finished (e.g. React running the effect twice):
      // let the next run load it again rather than leave it "Loading..." for ever.
      if (!finished) mgrLoadedReviewId.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReviewId, activeTab]);

  // ---------- Save manager review ----------

  const saveManagerReview = useCallback(async (nextStep?: number) => {
    if (!selectedReviewId) return;
    setMgrSaving(true);
    setMgrSaveMsg("");
    try {
      const payload = {
        responsibilities: mgrForm.responsibilities.map((r) => ({
          id: r.id,
          manager_evidence: r.manager_evidence,
          manager_rating: r.manager_rating,
          manager_example: r.manager_example,
        })),
        strengths: mgrForm.strengths.filter((s) => s.strength.trim()),
        development_areas: mgrForm.development_areas.filter((d) => d.area.trim()),
        behaviours: mgrForm.behaviours.map((b) => ({
          id: b.id,
          manager_evidence: b.manager_evidence,
          manager_rating: b.manager_rating,
          manager_example: b.manager_example,
        })),
        feedback_themes: mgrForm.feedback_themes.filter((t) => t.theme.trim()),
        objectives: mgrForm.objectives.filter((o) => o.objective.trim()),
        development_plan: mgrForm.development_plan.filter((p) => p.support_needed.trim()),
        overall_manager_rating: mgrForm.overall_manager_rating,
        overall_example: mgrForm.overall_example,
        calibration_summary: mgrForm.calibration_summary || null,
        feedback_synthesis: mgrForm.feedback_synthesis || null,
        risk_flight_risk: mgrForm.risk_flight_risk || null,
        risk_retention_priority: mgrForm.risk_retention_priority || null,
        risk_successor: mgrForm.risk_successor || null,
        risk_single_point_of_failure: mgrForm.risk_single_point_of_failure,
        risk_commentary: mgrForm.risk_commentary || null,
        check_in_cadence: mgrForm.check_in_cadence || "",
        check_in_minutes: mgrForm.check_in_minutes,
        check_in_time: mgrForm.check_in_time,
        sections: [
          { section_number: currentStep, section_type: "manager_review", is_complete: true },
        ],
      };
      await api.put(`/api/reviews/${selectedReviewId}/manager-review`, payload);
      setMgrCompletedSteps((prev) => new Set(prev).add(currentStep));
      setMgrSaveMsg("Saved");
      setTimeout(() => setMgrSaveMsg(""), 2000);
      if (nextStep !== undefined) goToStep(nextStep);
    } catch (err) {
      setMgrSaveMsg(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setMgrSaving(false);
    }
  }, [selectedReviewId, mgrForm, currentStep, goToStep]);

  const submitManagerReview = useCallback(async () => {
    if (!selectedReviewId) return;
    setMgrSaving(true);
    try {
      await saveManagerReview();
      await api.post(`/api/reviews/${selectedReviewId}/manager-review/submit`);
      setMgrSaveMsg("Manager review submitted!");
      setMgrShowConfirm(false);
      const data = await api.get<ReviewFull>(`/api/reviews/${selectedReviewId}/manager-review`);
      setMgrData(data);
      setReviewData(data);
      // Straight on to booking the review meeting.
      setSearchParams({ review: String(selectedReviewId), book: "1" }, { replace: true });
    } catch (err) {
      setMgrSaveMsg(err instanceof ApiError ? err.message : "Submit failed");
      setMgrShowConfirm(false);
    } finally {
      setMgrSaving(false);
    }
  }, [selectedReviewId, saveManagerReview, setSearchParams]);

  // ---------- Save self-assessment ----------

  const saveSelfAssessment = useCallback(async (nextStep?: number) => {
    if (!selectedReviewId) return;
    setSaving(true);
    setSaveMsg("");
    try {
      const payload = {
        responsibilities: formState.responsibilities.filter((r) => r.responsibility_area.trim()),
        strengths: formState.strengths.filter((s) => s.strength.trim()),
        development_areas: formState.development_areas.filter((d) => d.area.trim()),
        could_do_better: formState.could_do_better,
        behaviours: formState.behaviours.map((b) => ({
          id: b.id,
          behaviour_name: b.behaviour_name,
          employee_example: b.employee_example,
          employee_rating: b.employee_rating,
        })),
        objectives: formState.objectives.filter((o) => o.objective.trim()),
        additional_comments: formState.additional_comments,
        role_aspirations: formState.role_aspirations,
        overall_self_rating: formState.overall_self_rating,
        top_priority_1: formState.top_priority_1,
        top_priority_2: formState.top_priority_2,
        sections: [
          { section_number: currentStep, section_type: "self_assessment", is_complete: true },
        ],
      };
      await api.put(`/api/reviews/${selectedReviewId}/self-assessment`, payload);
      setCompletedSteps((prev) => new Set(prev).add(currentStep));
      setSaveMsg("Saved");
      setTimeout(() => setSaveMsg(""), 2000);
      if (nextStep !== undefined) goToStep(nextStep);
    } catch (err) {
      setSaveMsg(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }, [selectedReviewId, formState, currentStep, goToStep]);

  const submitSelfAssessment = useCallback(async () => {
    if (!selectedReviewId) return;
    setSaving(true);
    try {
      // Save current state first
      await api.put(`/api/reviews/${selectedReviewId}/self-assessment`, {
        responsibilities: formState.responsibilities.filter((r) => r.responsibility_area.trim()),
        strengths: formState.strengths.filter((s) => s.strength.trim()),
        development_areas: formState.development_areas.filter((d) => d.area.trim()),
        could_do_better: formState.could_do_better,
        behaviours: formState.behaviours.map((b) => ({
          id: b.id,
          behaviour_name: b.behaviour_name,
          employee_example: b.employee_example,
          employee_rating: b.employee_rating,
        })),
        objectives: formState.objectives.filter((o) => o.objective.trim()),
        additional_comments: formState.additional_comments,
        role_aspirations: formState.role_aspirations,
        overall_self_rating: formState.overall_self_rating,
        top_priority_1: formState.top_priority_1,
        top_priority_2: formState.top_priority_2,
      });
      // Then submit
      await api.post(`/api/reviews/${selectedReviewId}/self-assessment/submit`);
      setSaveMsg("");
      setJustSubmitted(true);
      // Reload review data to get updated status
      const data = await api.get<ReviewFull>(`/api/reviews/${selectedReviewId}/self-assessment`);
      setReviewData(data);
      setShowConfirm(false);
    } catch (err) {
      setSaveMsg(err instanceof ApiError ? err.message : "Submit failed");
      setShowConfirm(false);
    } finally {
      setSaving(false);
    }
  }, [selectedReviewId, formState]);

  // ---------- Save employee response ----------

  const saveEmployeeResponse = useCallback(async () => {
    if (!selectedReviewId) return;
    setResponseSaving(true);
    try {
      await api.put(`/api/reviews/${selectedReviewId}/employee-response`, {
        employee_response: employeeResponse,
      });
      setResponseSaved(true);
      setSaveMsg("Response saved");
      setTimeout(() => setSaveMsg(""), 3000);
    } catch (err) {
      setSaveMsg(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setResponseSaving(false);
    }
  }, [selectedReviewId, employeeResponse]);

  // ---------- Save & submit colleague feedback ----------

  const saveColleagueFeedback = useCallback(async () => {
    if (!feedbackData) return;
    setSaving(true);
    try {
      await api.put(`/api/reviews/colleague-feedback/${feedbackData.id}`, {
        responses: feedbackData.responses.map((r) => ({
          id: r.id,
          answer: r.answer,
          rating: r.rating,
        })),
      });
      setSaveMsg("Saved");
      setTimeout(() => setSaveMsg(""), 2000);
    } catch (err) {
      setSaveMsg(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }, [feedbackData]);

  const submitColleagueFeedback = useCallback(async () => {
    if (!feedbackData) return;
    setSaving(true);
    try {
      // Save first
      await api.put(`/api/reviews/colleague-feedback/${feedbackData.id}`, {
        responses: feedbackData.responses.map((r) => ({
          id: r.id,
          answer: r.answer,
          rating: r.rating,
        })),
      });
      // Then submit
      await api.post(`/api/reviews/colleague-feedback/${feedbackData.id}/submit`);
      setSaveMsg("");
      setFeedbackData({ ...feedbackData, status: "submitted" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setSaveMsg(err instanceof ApiError ? err.message : "Submit failed");
    } finally {
      setSaving(false);
    }
  }, [feedbackData]);

  // ---------- Determine mode ----------

  const isColleagueFeedbackMode = !!colleagueFeedbackId;
  const isReviewMode = !!selectedReviewId;

  const isSelfAssessmentEditable = reviewData
    ? ["not_started", "self_assessment_in_progress"].includes(reviewData.status)
    : false;

  const showSelfAssessmentWizard = isReviewMode && activeTab === "self-assessment" && isSelfAssessmentEditable;

  // Steps with nothing written yet, shown before submitting (step 7, "anything
  // else", is optional and never listed).
  const filled = (v: string | null | undefined) => !!v && v.trim().length > 0;
  const selfAssessmentGaps = selfAssessmentSteps().filter(({ n }) => {
    switch (n) {
      case 1: return !formState.responsibilities.some((r) => filled(r.employee_summary) || r.self_rating != null);
      case 2: return !formState.strengths.some((r) => filled(r.strength));
      case 3: return !formState.development_areas.some((r) => filled(r.area));
      case 4: return !filled(formState.could_do_better);
      case 5: return !formState.behaviours.some((b) => filled(b.employee_example) || b.employee_rating != null);
      case 6: return !formState.objectives.some((o) => filled(o.objective));
      case 8: return !filled(formState.role_aspirations);
      default: return false;
    }
  });
  // Where "Continue" picks up: the first step not yet saved.
  const resumeStep = (() => {
    for (let n = 1; n <= TOTAL_STEPS; n += 1) if (!completedSteps.has(n)) return n;
    return TOTAL_STEPS;
  })();

  // The manager may draft (and re-open) the review while colleague feedback is
  // still being collected — they just cannot submit it until peers are done.
  const isManagerReviewEditable = reviewData
    ? ["colleague_feedback_in_progress", "manager_review_in_progress", "colleague_feedback_complete"].includes(reviewData.status)
    : false;

  // Mirrors the backend rule in check_colleague_feedback_complete(): a peer
  // whose deadline has passed is treated as complete and does not block.
  const todayIso = new Date().toISOString().slice(0, 10);
  const pendingColleagues = colleagueFeedbackList.filter(
    (c) => c.status !== "submitted" && (!c.deadline || c.deadline > todayIso),
  );
  const submittedColleagueCount = colleagueFeedbackList.filter((c) => c.status === "submitted").length;
  const colleagueFeedbackOutstanding =
    reviewData?.status === "colleague_feedback_in_progress" && pendingColleagues.length > 0;

  const showManagerReviewWizard = isReviewMode && activeTab === "manager-review";

  // =========================================================================
  // RENDER: Colleague Feedback Form
  // =========================================================================

  if (isColleagueFeedbackMode) {
    if (feedbackLoading) {
      return (
        <div className="flex min-h-dvh flex-col bg-white safe-x">
          <header className="safe-top px-5 pt-4 pb-3">
            <button type="button" onClick={goToList} className="text-sm text-pme-blue mb-2">&larr; {fromEmployee ? "Back to Employee" : "Back"}</button>
            <h1 className="text-2xl font-semibold text-gray-900">Colleague Feedback</h1>
          </header>
          <main className="flex-1 px-5 py-4 pb-24">
            <p className="text-sm text-gray-400">Loading...</p>
          </main>
          <BottomNav />
        </div>
      );
    }

    if (!feedbackData) {
      return (
        <div className="flex min-h-dvh flex-col bg-white safe-x">
          <header className="safe-top px-5 pt-4 pb-3">
            <button type="button" onClick={goToList} className="text-sm text-pme-blue mb-2">&larr; {fromEmployee ? "Back to Employee" : "Back"}</button>
            <h1 className="text-2xl font-semibold text-gray-900">Colleague Feedback</h1>
          </header>
          <main className="flex-1 px-5 py-4 pb-24">
            <p className="text-sm text-red-600">{reviewError || "Feedback not found"}</p>
          </main>
          <BottomNav />
        </div>
      );
    }

    const isSubmitted = feedbackData.status === "submitted";

    return (
      <div className="flex min-h-dvh flex-col bg-pme-paper safe-x">
        <header className="safe-top px-5 pt-4 pb-3">
          <button type="button" onClick={goToList} className="text-sm text-pme-blue mb-2">&larr; {fromEmployee ? "Back to Employee" : "Performance"}</button>
          <p className="eyebrow">Colleague feedback</p>
          <h1 className="font-display text-3xl text-pme-navy">Feedback on {feedbackData.employee_name}</h1>
          {feedbackData.deadline && !isSubmitted && (
            <p className="mt-0.5 text-xs text-gray-500">
              Please send it by {formatDate(feedbackData.deadline, "long")} · {dueText(feedbackData.deadline)}
            </p>
          )}
        </header>

        <main className="mx-auto w-full max-w-2xl flex-1 overflow-y-auto px-5 py-4 pb-24 space-y-4">
          {isSubmitted ? (
            <div className="rounded-xl border border-green-200 bg-green-50 p-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-green-800">
                <CheckCircle2 size={18} strokeWidth={2} aria-hidden />
                Thank you, your feedback has been sent.
              </p>
              <p className="mt-1 text-sm text-green-700">
                {feedbackData.employee_name}'s manager will use it in their review. {feedbackData.employee_name} sees themes only, never your name.
              </p>
            </div>
          ) : (
            <div className="rounded-xl border border-pme-blue/20 bg-white p-4 shadow-sm">
              <p className="text-sm text-gray-700">
                {feedbackData.employee_name}'s manager has asked for your view as part of their {appraisalName(feedbackData.appraisal_year, false).toLowerCase()}. It takes about 10 minutes.
              </p>
              <ul className="mt-2 space-y-1 text-xs text-gray-500">
                <li className="flex gap-1.5"><Eye size={13} aria-hidden className="mt-0.5 shrink-0 text-pme-blue" />{feedbackData.employee_name} sees your feedback as part of overall themes, never with your name. Their manager and HR can see who said what.</li>
                <li className="flex gap-1.5"><Save size={13} aria-hidden className="mt-0.5 shrink-0 text-pme-blue" />Save a draft and come back, or send it when you're done.</li>
                {feedbackData.focus_area && feedbackData.focus_area !== "general" && (
                  <li className="flex gap-1.5"><span aria-hidden className="text-pme-blue">•</span>Please focus on: {feedbackData.focus_area}</li>
                )}
              </ul>
            </div>
          )}
          {feedbackData.responses.map((resp, idx) => (
            <SectionCard key={resp.id} title={resp.question || `Question ${idx + 1}`}>
              <textarea
                rows={4}
                disabled={isSubmitted}
                value={resp.answer || ""}
                onChange={(e) => {
                  const updated = [...feedbackData.responses];
                  updated[idx] = { ...resp, answer: e.target.value };
                  setFeedbackData({ ...feedbackData, responses: updated });
                }}
                placeholder="Your feedback..."
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue disabled:bg-gray-50"
              />
              <div className="mt-2">
                <label className="text-xs text-gray-500 mb-1 block">{feedbackData.rating_words ? "How do they do here?" : "Rating"}</label>
                <RatingInput
                  value={resp.rating}
                  scale={feedbackData.rating_words ? "colleague" : "company"}
                  disabled={isSubmitted}
                  onChange={(v) => {
                    const updated = [...feedbackData.responses];
                    updated[idx] = { ...resp, rating: v };
                    setFeedbackData({ ...feedbackData, responses: updated });
                  }}
                />
              </div>
            </SectionCard>
          ))}

          {saveMsg && (
            <p className={`text-sm ${saveMsg.includes("fail") || saveMsg.includes("Failed") ? "text-red-600" : "text-green-600"}`}>
              {saveMsg}
            </p>
          )}

          {!isSubmitted && (
            <div className="flex gap-2">
              <button
                type="button"
                disabled={saving}
                onClick={saveColleagueFeedback}
                className="flex-1 rounded-lg bg-gray-100 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
              >
                Save draft
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={submitColleagueFeedback}
                className="flex-1 rounded-lg bg-pme-blue py-2.5 text-sm font-semibold text-white hover:bg-pme-blue/90 disabled:opacity-50"
              >
                {saving ? "Sending..." : "Send my feedback"}
              </button>
            </div>
          )}
        </main>
        <BottomNav />
      </div>
    );
  }

  // =========================================================================
  // RENDER: Review Detail Mode
  // =========================================================================

  /**
   * Admin-only "Preview as employee" control + banner.
   * Rendered in the read-only review detail views (normal and historical) —
   * NOT in the self-assessment wizard, which an admin never reaches.
   * Fetches the subject's own redacted payload and renders it through this
   * same component, so what is shown is literally what the API returns.
   */
  /** Manager/admin control to release feedback on a completed review. */
  const renderReleaseFeedbackBar = () => {
    if (!reviewData) return null;
    const isManager = reviewData.manager_id != null && reviewData.manager_id === currentUser?.employee_id;
    if (!isAdmin && !isManager) return null;
    if (reviewData.status !== "completed") return null;
    if (reviewData.feedback_released_at) {
      return (
        <p className="mb-2 text-xs text-green-700">
          Feedback released to the employee on{" "}
          {DATE_FMT.format(new Date(reviewData.feedback_released_at))}.
        </p>
      );
    }
    return (
      <div className="mb-2 rounded-lg border border-amber-300 bg-amber-50 p-2.5">
        <p className="text-xs font-semibold text-amber-800">Feedback not yet released</p>
        <p className="mt-0.5 text-[11px] text-amber-700">
          The employee cannot see your review content until you release it. This cannot be undone.
        </p>
        <button
          type="button"
          disabled={releasing}
          onClick={async () => {
            if (!selectedReviewId) return;
            if (!window.confirm(
              "Release your feedback to the employee? They will be emailed and will see the review content. This cannot be undone."
            )) return;
            setReleasing(true); setReleaseError("");
            try {
              await api.post(`/api/reviews/${selectedReviewId}/release-feedback`);
              const fresh = await api.get<ReviewFull>(`/api/reviews/${selectedReviewId}/self-assessment`);
              setReviewData(fresh);
            } catch (err) {
              setReleaseError(err instanceof ApiError ? err.message : "Release failed");
            } finally {
              setReleasing(false);
            }
          }}
          className="mt-2 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
        >
          {releasing ? "Releasing…" : "Release feedback to employee"}
        </button>
        {releaseError && <span className="ml-2 text-xs text-red-600">{releaseError}</span>}
      </div>
    );
  };

  const renderEmployeePreviewBar = () => {
    if (!isAdmin) return null;
    const subjectName = reviewData?.employee_name ?? "the employee";
    return (
      <>
        <div className="mb-2">
          <button
            type="button"
            disabled={previewLoading}
            onClick={async () => {
              if (subjectPreview) {
                if (adminReviewData) setReviewData(adminReviewData);
                setSubjectPreview(false);
                setPreviewError("");
                return;
              }
              if (!selectedReviewId) return;
              setPreviewLoading(true);
              setPreviewError("");
              try {
                const data = await api.get<ReviewFull>(`/api/reviews/${selectedReviewId}/subject-preview`);
                setAdminReviewData(reviewData);
                setReviewData(data);
                setSubjectPreview(true);
              } catch (err) {
                setPreviewError(err instanceof ApiError ? err.message : "Preview failed");
              } finally {
                setPreviewLoading(false);
              }
            }}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${
              subjectPreview
                ? "border-amber-400 bg-amber-50 text-amber-800"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
            } disabled:opacity-50`}
          >
            {previewLoading
              ? "Loading…"
              : subjectPreview
                ? "Exit employee preview"
                : "Preview as employee"}
          </button>
          {previewError && <span className="ml-2 text-xs text-red-600">{previewError}</span>}
        </div>
        {subjectPreview && (
          <div className="mb-2 flex items-start justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 p-2.5">
            <p className="text-xs font-semibold text-amber-800">
              Employee preview — this is exactly what {subjectName} receives from the API
            </p>
            <button
              type="button"
              onClick={() => {
                if (adminReviewData) setReviewData(adminReviewData);
                setSubjectPreview(false);
                setPreviewError("");
              }}
              className="shrink-0 rounded border border-amber-400 px-2 py-0.5 text-[11px] font-medium text-amber-800 hover:bg-amber-100"
            >
              Exit preview
            </button>
          </div>
        )}
      </>
    );
  };

  if (isReviewMode) {
    if (reviewLoading) {
      return (
        <div className="flex min-h-dvh flex-col bg-white safe-x">
          <header className="safe-top px-5 pt-4 pb-3">
            <button type="button" onClick={goToList} className="text-sm text-pme-blue mb-2">&larr; {fromEmployee ? "Back to Employee" : "Back to appraisals"}</button>
            <h1 className="text-2xl font-semibold text-gray-900">Appraisal</h1>
          </header>
          <main className="flex-1 px-5 py-4 pb-24">
            <p className="text-sm text-gray-400">Loading...</p>
          </main>
          <BottomNav />
        </div>
      );
    }

    if (reviewError || !reviewData) {
      return (
        <div className="flex min-h-dvh flex-col bg-white safe-x">
          <header className="safe-top px-5 pt-4 pb-3">
            <button type="button" onClick={goToList} className="text-sm text-pme-blue mb-2">&larr; {fromEmployee ? "Back to Employee" : "Back to appraisals"}</button>
            <h1 className="text-2xl font-semibold text-gray-900">Appraisal</h1>
          </header>
          <main className="flex-1 px-5 py-4 pb-24">
            <p className="text-sm text-red-600">{reviewError || "Review not found"}</p>
          </main>
          <BottomNav />
        </div>
      );
    }

    // ---------- Manager Review Wizard ----------

    if (activeTab === "meeting" && selectedReviewId) {
      const jdShown = jdOpen && !!jobDescription && hasJobDescription(jobDescription);
      return (
        <div className={`flex min-h-dvh flex-col bg-pme-paper safe-x ${jdShown ? "lg:pr-[min(46vw,760px)]" : ""}`}>
          {jdShown && jobDescription && <JobDescriptionPanel jd={jobDescription} title="Their job description" onClose={() => setJdOpen(false)} />}
          <header className="safe-top meeting-noprint px-5 pt-4 pb-2 pr-28 lg:pr-56">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setSearchParams({ review: String(selectedReviewId) }, { replace: true })}
                className="text-sm text-pme-blue"
              >
                &larr; Back to the appraisal
              </button>
              {jobDescription && hasJobDescription(jobDescription) && (
                <button
                  type="button"
                  onClick={() => setJdOpen((o) => !o)}
                  aria-pressed={jdOpen}
                  className={`inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold ${
                    jdOpen ? "border-pme-navy bg-pme-navy text-white" : "border-pme-blue/30 bg-white text-pme-blue hover:bg-pme-sky"
                  }`}
                >
                  <FileText size={14} aria-hidden />
                  {jdOpen ? "Hide job description" : "View job description"}
                </button>
              )}
            </div>
          </header>
          <main className="mx-auto w-full max-w-3xl flex-1 px-5 pb-24">
            <MeetingGuide reviewId={selectedReviewId} />
          </main>
          <BottomNav />
        </div>
      );
    }

    if (showManagerReviewWizard) {
      if (mgrLoading) {
        return (
          <div className="flex min-h-dvh flex-col bg-white safe-x">
            <header className="safe-top px-5 pt-4 pb-3">
              <button type="button" onClick={() => navigate("/performance?tab=team-reviews")} className="text-sm text-purple-600 mb-2">&larr; Back to appraisals</button>
              <h1 className="text-xl font-semibold text-gray-900">Manager Review</h1>
            </header>
            <main className="flex-1 px-5 py-4 pb-24">
              <p className="text-sm text-gray-400">Loading...</p>
            </main>
            <BottomNav />
          </div>
        );
      }

      if (!mgrData) {
        return (
          <div className="flex min-h-dvh flex-col bg-white safe-x">
            <header className="safe-top px-5 pt-4 pb-3">
              <button type="button" onClick={() => navigate("/performance?tab=team-reviews")} className="text-sm text-purple-600 mb-2">&larr; Back to appraisals</button>
              <h1 className="text-xl font-semibold text-gray-900">Manager Review</h1>
            </header>
            <main className="flex-1 px-5 py-4 pb-24">
              <p className="text-sm text-red-600">{reviewError || "Review not found"}</p>
            </main>
            <BottomNav />
          </div>
        );
      }

      const empStrengths = mgrData.strengths.filter((s) => s.source !== "manager");
      const empDevAreas = mgrData.development_areas.filter((d) => d.source !== "manager");
      const empObjectives = mgrData.objectives.filter((o) => o.source !== "manager_agreed");
      const editable = isManagerReviewEditable;
      const mgrJdAvailable = !!jobDescription && hasJobDescription(jobDescription);
      const mgrJdShown = jdOpen && mgrJdAvailable;

      return (
        <div className={`flex min-h-dvh flex-col bg-gray-50 safe-x ${mgrJdShown ? "lg:pr-[min(46vw,760px)]" : ""}`}>
          {mgrJdShown && jobDescription && (
            <JobDescriptionPanel jd={jobDescription} title={`${mgrData.employee_name.split(" ")[0]}'s job description`} onClose={() => setJdOpen(false)} />
          )}
          <header className="safe-top bg-white border-b border-gray-200 px-5 pt-4 pb-3">
            <button type="button" onClick={() => navigate("/performance?tab=team-reviews")} className="text-sm text-purple-600 mb-2">&larr; Back to appraisals</button>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{mgrData.employee_name} · {appraisalName(mgrData.appraisal_year)}</h1>
                <p className="text-sm text-gray-500">Your manager review{mgrData.cycle_name ? ` · ${mgrData.cycle_name}` : ""}</p>
              </div>
              <div className="flex items-center gap-2">
                {mgrJdAvailable && (
                  <button
                    type="button"
                    onClick={() => setJdOpen((o) => !o)}
                    aria-pressed={jdOpen}
                    className={`inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold ${
                      jdOpen ? "border-pme-navy bg-pme-navy text-white" : "border-purple-200 bg-white text-purple-700 hover:bg-purple-50"
                    }`}
                  >
                    <FileText size={14} aria-hidden />
                    {jdOpen ? "Hide job description" : "View job description"}
                  </button>
                )}
                <StatusBadge status={mgrData.status} />
              </div>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto px-5 py-4 pb-24">
            <ManagerProgressBar step={currentStep} />
            <ManagerStepNav step={currentStep} onStep={goToStep} completedSteps={mgrCompletedSteps} />

            <div className="flex flex-col lg:flex-row gap-4">
              {/* Left panel — Employee self-assessment reference */}
              {currentStep <= 8 && (
                <div className="lg:w-2/5 order-2 lg:order-1">
                  <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4">
                    <h3 className="text-xs font-semibold text-blue-700 uppercase mb-3">Employee Self-Assessment</h3>

                    {currentStep === 1 && (
                      <div className="space-y-2 text-sm">
                        <ReadOnlyField label="Their overall view of their year" value={mgrData.overall_self_rating ? selfRatingText(mgrData.overall_self_rating, mgrData.rating_words) : null} />
                        <ReadOnlyField label="Top Priority 1" value={mgrData.top_priority_1} />
                        <ReadOnlyField label="Top Priority 2" value={mgrData.top_priority_2} />
                        <ReadOnlyField label="Role Aspirations" value={mgrData.role_aspirations} />
                        <ReadOnlyField label="Could Do Better" value={mgrData.could_do_better} />
                        <ReadOnlyField label="Additional Comments" value={mgrData.additional_comments} />
                        {!mgrData.overall_self_rating && !mgrData.top_priority_1 && (
                          <p className="text-xs text-gray-400 italic">No self-assessment data available.</p>
                        )}
                      </div>
                    )}

                    {currentStep === 2 && (
                      <div className="space-y-3">
                        {mgrData.responsibilities.length === 0 ? (
                          <p className="text-xs text-gray-400 italic">No responsibilities entered.</p>
                        ) : mgrData.responsibilities.map((r, i) => (
                          <div key={i} className="border-b border-blue-100 pb-2 last:border-0">
                            <p className="text-sm font-medium text-gray-800">{r.responsibility_area}</p>
                            <p className="text-xs text-gray-600 mt-0.5">{r.employee_summary}</p>
                            {r.self_rating != null && <p className="text-xs text-blue-600 mt-0.5">Their view: {selfRatingText(r.self_rating, mgrData.rating_words)}</p>}
                          </div>
                        ))}
                      </div>
                    )}

                    {currentStep === 3 && (
                      <div className="space-y-3">
                        {empStrengths.length === 0 ? (
                          <p className="text-xs text-gray-400 italic">No strengths entered.</p>
                        ) : empStrengths.map((s, i) => (
                          <div key={i} className="border-b border-blue-100 pb-2 last:border-0">
                            <p className="text-sm font-medium text-gray-800">{s.strength}</p>
                            <p className="text-xs text-gray-600 mt-0.5">{s.still_valid_example}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {currentStep === 4 && (
                      <div className="space-y-3">
                        {empDevAreas.length === 0 ? (
                          <p className="text-xs text-gray-400 italic">No development areas entered.</p>
                        ) : empDevAreas.map((d, i) => (
                          <div key={i} className="border-b border-blue-100 pb-2 last:border-0">
                            <p className="text-sm font-medium text-gray-800">{d.area}</p>
                            <p className="text-xs text-gray-600 mt-0.5">{d.employee_progress}</p>
                            {d.self_rating != null && <p className="text-xs text-blue-600 mt-0.5">Their view: {selfRatingText(d.self_rating, mgrData.rating_words)}</p>}
                          </div>
                        ))}
                      </div>
                    )}

                    {currentStep === 5 && (
                      <div className="space-y-3 text-sm">
                        <p className="text-xs text-gray-500 mb-2">
                          Colleague feedback — {submittedColleagueCount} of {colleagueFeedbackList.length} submitted
                        </p>
                        {colleagueFeedbackList.length === 0 ? (
                          <p className="text-xs text-gray-400 italic">No colleague feedback assigned.</p>
                        ) : colleagueFeedbackList.map((c) => (
                          <div key={c.id} className="rounded-lg border border-blue-100 bg-white p-2.5">
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-sm font-medium text-gray-800">{c.colleague_name}</p>
                              <StatusBadge status={c.status} />
                            </div>
                            {c.focus_area && (
                              <p className="text-xs text-gray-500 mt-0.5">Focus: {c.focus_area}</p>
                            )}
                            {c.status === "submitted" ? (
                              c.responses.length === 0 ? (
                                <p className="text-xs text-gray-400 italic mt-1.5">No answers recorded.</p>
                              ) : (
                                <div className="mt-2 space-y-2">
                                  {c.responses.map((r) => (
                                    <div key={r.id} className="border-t border-blue-50 pt-1.5">
                                      <p className="text-xs font-medium text-gray-600">{r.question}</p>
                                      {r.answer && (
                                        <p className="text-xs text-gray-700 mt-0.5 whitespace-pre-wrap">{r.answer}</p>
                                      )}
                                      {r.rating != null && (
                                        <p className="text-xs text-blue-600 mt-0.5">Their view: {selfRatingText(r.rating, mgrData.rating_words)}</p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )
                            ) : (
                              <p className="text-xs text-amber-600 italic mt-1.5">
                                Awaiting response{c.deadline ? ` · due ${c.deadline}` : ""}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {currentStep === 6 && (
                      <div className="space-y-3">
                        {mgrData.behaviours.length === 0 ? (
                          <p className="text-xs text-gray-400 italic">No behaviours entered.</p>
                        ) : mgrData.behaviours.map((b, i) => (
                          <div key={i} className="border-b border-blue-100 pb-2 last:border-0">
                            <p className="text-sm font-medium text-gray-800">{b.behaviour_name}</p>
                            <p className="text-xs text-gray-600 mt-0.5">{b.employee_example}</p>
                            {b.employee_rating != null && <p className="text-xs text-blue-600 mt-0.5">Their view: {selfRatingText(b.employee_rating, mgrData.rating_words)}</p>}
                          </div>
                        ))}
                      </div>
                    )}

                    {currentStep === 7 && (
                      <div className="space-y-3">
                        {empObjectives.length === 0 ? (
                          <p className="text-xs text-gray-400 italic">No objectives entered.</p>
                        ) : empObjectives.map((o, i) => (
                          <div key={i} className="border-b border-blue-100 pb-2 last:border-0">
                            <p className="text-sm text-gray-700">{o.objective}</p>
                            {o.how_measured && <p className="text-xs text-gray-500">Measured: {o.how_measured}</p>}
                          </div>
                        ))}
                      </div>
                    )}

                    {currentStep === 8 && (
                      <div className="space-y-2 text-sm">
                        <ReadOnlyField label="Could Do Better" value={mgrData.could_do_better} />
                        <ReadOnlyField label="Role Aspirations" value={mgrData.role_aspirations} />
                        {!mgrData.could_do_better && !mgrData.role_aspirations && (
                          <p className="text-xs text-gray-400 italic">No self-assessment context for this step.</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Right panel — Manager form */}
              <div className={currentStep <= 8 ? "lg:w-3/5 order-1 lg:order-2" : "w-full"}>
                {/* Step 1: Calibration Summary */}
                {currentStep === 1 && (
                  <SectionCard title="Step 1: Summary of the year">
                    <p className="text-sm text-gray-700 mb-1">In a few sentences, how did {mgrData.employee_name.split(" ")[0]}'s year go?</p>
                    <ul className="mb-3 space-y-0.5 text-xs text-gray-500">
                      <li>• The main things they achieved.</li>
                      <li>• How they went about it: the way they worked with others.</li>
                      <li>• Anything that held them back.</li>
                    </ul>
                    <p className="mb-2 text-xs text-gray-500">Plain words are fine. You'll go through it with them in the review meeting. Their own view is on the left.</p>
                    <textarea
                      rows={6}
                      disabled={!editable}
                      placeholder="e.g. A strong year. Took over the supplier invoices in May and cleared the backlog by July..."
                      value={mgrForm.calibration_summary}
                      onChange={(e) => setMgrForm({ ...mgrForm, calibration_summary: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-50"
                    />
                  </SectionCard>
                )}

                {/* Step 2: Key Responsibilities */}
                {currentStep === 2 && (
                  <SectionCard title="Step 2: Key Responsibilities">
                    <ManagerRatingGuide />
                    <p className="text-xs text-gray-500 mb-3">For each responsibility, write what you saw and give your rating.</p>
                    {mgrForm.responsibilities.map((row, idx) => (
                      <div key={idx} className="mb-4 rounded-lg border border-gray-100 bg-gray-50 p-3">
                        <p className="text-sm font-medium text-gray-800 mb-1">{row.responsibility_area}</p>
                        <JdAreaHint kra={findKra(onlineJd(jobDescription), row.responsibility_area)} />
                        <p className="text-xs text-gray-500 mb-2">{row.employee_summary}</p>
                        {row.self_rating != null && <p className="text-xs text-blue-600 mb-2">Their view: {selfRatingText(row.self_rating, mgrData.rating_words)}</p>}
                        <textarea
                          rows={3}
                          disabled={!editable}
                          placeholder="What did you see? How well did they do this part of the job?"
                          value={row.manager_evidence || ""}
                          onChange={(e) => {
                            const updated = [...mgrForm.responsibilities];
                            updated[idx] = { ...row, manager_evidence: e.target.value };
                            setMgrForm({ ...mgrForm, responsibilities: updated });
                          }}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm mb-2 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-50"
                        />
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">Manager Rating</label>
                          <RatingInput
                            value={row.manager_rating ?? null}
                            disabled={!editable}
                            onChange={(v) => {
                              const updated = [...mgrForm.responsibilities];
                              updated[idx] = { ...row, manager_rating: v };
                              setMgrForm({ ...mgrForm, responsibilities: updated });
                            }}
                          />
                          <ExampleBox
                            rating={row.manager_rating}
                            value={row.manager_example}
                            disabled={!editable}
                            subject="this responsibility"
                            onChange={(text) => {
                              const updated = [...mgrForm.responsibilities];
                              updated[idx] = { ...row, manager_example: text };
                              setMgrForm({ ...mgrForm, responsibilities: updated });
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </SectionCard>
                )}

                {/* Step 3: Manager's Strengths */}
                {currentStep === 3 && (
                  <SectionCard title="Step 3: Manager's View of Strengths">
                    <p className="text-xs text-gray-500 mb-3">Identify strengths you've observed with evidence and impact.</p>
                    {mgrForm.strengths.map((row, idx) => (
                      <div key={idx} className="mb-4 rounded-lg border border-gray-100 bg-gray-50 p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-500">#{idx + 1}</span>
                          {mgrForm.strengths.length > 1 && editable && (
                            <button type="button" onClick={() => {
                              setMgrForm({ ...mgrForm, strengths: mgrForm.strengths.filter((_, i) => i !== idx) });
                            }} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                          )}
                        </div>
                        <input
                          type="text"
                          disabled={!editable}
                          placeholder="Strength"
                          value={row.strength}
                          onChange={(e) => {
                            const updated = [...mgrForm.strengths];
                            updated[idx] = { ...row, strength: e.target.value };
                            setMgrForm({ ...mgrForm, strengths: updated });
                          }}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm mb-2 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-50"
                        />
                        <textarea
                          rows={3}
                          disabled={!editable}
                          placeholder="Describe the evidence and impact of this strength..."
                          value={row.evidence_impact}
                          onChange={(e) => {
                            const updated = [...mgrForm.strengths];
                            updated[idx] = { ...row, evidence_impact: e.target.value };
                            setMgrForm({ ...mgrForm, strengths: updated });
                          }}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-50"
                        />
                      </div>
                    ))}
                    {editable && (
                      <button type="button" onClick={() => {
                        setMgrForm({ ...mgrForm, strengths: [...mgrForm.strengths, { strength: "", evidence_impact: "" }] });
                      }} className="mt-1 text-sm text-purple-600 hover:underline">+ Add Strength</button>
                    )}
                  </SectionCard>
                )}

                {/* Step 4: Manager's Development Areas */}
                {currentStep === 4 && (
                  <SectionCard title="Step 4: Manager's View of Development Areas">
                    <p className="text-xs text-gray-500 mb-3">Identify development areas with current state and what good looks like.</p>
                    {mgrForm.development_areas.map((row, idx) => (
                      <div key={idx} className="mb-4 rounded-lg border border-gray-100 bg-gray-50 p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-500">#{idx + 1}</span>
                          {mgrForm.development_areas.length > 1 && editable && (
                            <button type="button" onClick={() => {
                              setMgrForm({ ...mgrForm, development_areas: mgrForm.development_areas.filter((_, i) => i !== idx) });
                            }} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                          )}
                        </div>
                        <input
                          type="text"
                          disabled={!editable}
                          placeholder="Development area"
                          value={row.area}
                          onChange={(e) => {
                            const updated = [...mgrForm.development_areas];
                            updated[idx] = { ...row, area: e.target.value };
                            setMgrForm({ ...mgrForm, development_areas: updated });
                          }}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm mb-2 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-50"
                        />
                        <textarea
                          rows={2}
                          disabled={!editable}
                          placeholder="Where is the employee now in this area?"
                          value={row.manager_current_state}
                          onChange={(e) => {
                            const updated = [...mgrForm.development_areas];
                            updated[idx] = { ...row, manager_current_state: e.target.value };
                            setMgrForm({ ...mgrForm, development_areas: updated });
                          }}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm mb-2 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-50"
                        />
                        <textarea
                          rows={2}
                          disabled={!editable}
                          placeholder="Describe what good performance looks like..."
                          value={row.manager_what_good_looks_like}
                          onChange={(e) => {
                            const updated = [...mgrForm.development_areas];
                            updated[idx] = { ...row, manager_what_good_looks_like: e.target.value };
                            setMgrForm({ ...mgrForm, development_areas: updated });
                          }}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-50"
                        />
                      </div>
                    ))}
                    {editable && (
                      <button type="button" onClick={() => {
                        setMgrForm({ ...mgrForm, development_areas: [...mgrForm.development_areas, { area: "", manager_current_state: "", manager_what_good_looks_like: "" }] });
                      }} className="mt-1 text-sm text-purple-600 hover:underline">+ Add Development Area</button>
                    )}
                  </SectionCard>
                )}

                {/* Step 5: Colleague feedback summary */}
                {currentStep === 5 && (
                  <SectionCard title="Step 5: Colleague feedback summary">
                    <p className="text-xs text-gray-500 mb-3">Synthesise key themes from colleague feedback and add your perspective.</p>
                    <div className="mb-4">
                      <label className="text-xs font-medium text-gray-700 mb-1 block">Overall Feedback Synthesis</label>
                      <textarea
                        rows={4}
                        disabled={!editable}
                        placeholder="Synthesise the key themes from colleague feedback..."
                        value={mgrForm.feedback_synthesis}
                        onChange={(e) => setMgrForm({ ...mgrForm, feedback_synthesis: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-50"
                      />
                    </div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase">Themes</h4>
                    {mgrForm.feedback_themes.map((row, idx) => (
                      <div key={idx} className="mb-4 rounded-lg border border-gray-100 bg-gray-50 p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-500">Theme #{idx + 1}</span>
                          {mgrForm.feedback_themes.length > 1 && editable && (
                            <button type="button" onClick={() => {
                              setMgrForm({ ...mgrForm, feedback_themes: mgrForm.feedback_themes.filter((_, i) => i !== idx) });
                            }} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                          )}
                        </div>
                        <input
                          type="text"
                          disabled={!editable}
                          placeholder="Theme"
                          value={row.theme}
                          onChange={(e) => {
                            const updated = [...mgrForm.feedback_themes];
                            updated[idx] = { ...row, theme: e.target.value };
                            setMgrForm({ ...mgrForm, feedback_themes: updated });
                          }}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm mb-2 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-50"
                        />
                        <input
                          type="text"
                          disabled={!editable}
                          placeholder="Reviewers raising this theme"
                          value={row.reviewers_raising_it || ""}
                          onChange={(e) => {
                            const updated = [...mgrForm.feedback_themes];
                            updated[idx] = { ...row, reviewers_raising_it: e.target.value };
                            setMgrForm({ ...mgrForm, feedback_themes: updated });
                          }}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm mb-2 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-50"
                        />
                        <textarea
                          rows={2}
                          disabled={!editable}
                          placeholder="Your perspective on this theme..."
                          value={row.manager_view || ""}
                          onChange={(e) => {
                            const updated = [...mgrForm.feedback_themes];
                            updated[idx] = { ...row, manager_view: e.target.value };
                            setMgrForm({ ...mgrForm, feedback_themes: updated });
                          }}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-50"
                        />
                      </div>
                    ))}
                    {editable && (
                      <button type="button" onClick={() => {
                        setMgrForm({ ...mgrForm, feedback_themes: [...mgrForm.feedback_themes, { theme: "", reviewers_raising_it: "", manager_view: "" }] });
                      }} className="mt-1 text-sm text-purple-600 hover:underline">+ Add Theme</button>
                    )}
                  </SectionCard>
                )}

                {/* Step 6: Behaviours */}
                {currentStep === 6 && (
                  <SectionCard title="Step 6: Behaviours">
                    <ManagerRatingGuide compact />
                    <p className="text-xs text-gray-500 mb-3">For each behaviour, write what you saw and give your rating. A 4 or 5 needs a specific example.</p>
                    {mgrForm.behaviours.map((row, idx) => (
                      <div key={idx} className="mb-4 rounded-lg border border-gray-100 bg-gray-50 p-3">
                        <p className="text-sm font-medium text-gray-800 mb-1">{row.behaviour_name}</p>
                        <JdCompetencyHint competency={findCompetency(onlineJd(jobDescription), row.behaviour_name)} />
                        {row.employee_example && (
                          <p className="text-xs text-gray-500 mb-1">Employee: {row.employee_example}</p>
                        )}
                        {row.employee_rating && (
                          <p className="text-xs text-blue-600 mb-2">Their view: {selfRatingText(row.employee_rating, mgrData.rating_words)}</p>
                        )}
                        <textarea
                          rows={3}
                          disabled={!editable}
                          placeholder="How do they show this at work?"
                          value={row.manager_evidence || ""}
                          onChange={(e) => {
                            const updated = [...mgrForm.behaviours];
                            updated[idx] = { ...row, manager_evidence: e.target.value };
                            setMgrForm({ ...mgrForm, behaviours: updated });
                          }}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm mb-2 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-50"
                        />
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">Manager Rating</label>
                          <RatingInput
                            value={row.manager_rating ?? null}
                            disabled={!editable}
                            onChange={(v) => {
                              const updated = [...mgrForm.behaviours];
                              updated[idx] = { ...row, manager_rating: v };
                              setMgrForm({ ...mgrForm, behaviours: updated });
                            }}
                          />
                          <ExampleBox
                            rating={row.manager_rating}
                            value={row.manager_example}
                            disabled={!editable}
                            subject="this behaviour"
                            onChange={(text) => {
                              const updated = [...mgrForm.behaviours];
                              updated[idx] = { ...row, manager_example: text };
                              setMgrForm({ ...mgrForm, behaviours: updated });
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </SectionCard>
                )}

                {/* Step 7: Agreed Objectives */}
                {currentStep === 7 && (
                  <SectionCard title="Step 7: Objectives for next year">
                    <ObjectivesHighlight name={mgrData?.employee_name?.split(" ")[0]} />
                    <div className="mb-3 rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-900">
                      <p><span className="font-semibold">Objectives</span> are what they will <span className="font-semibold">deliver</span> at work: results you can check. "Cut stock-count time from 5 days to 2."</p>
                      <p className="mt-1"><span className="font-semibold">The development plan</span> (next step) is the <span className="font-semibold">help they get to grow</span>: training, coaching, time. "Excel course by March."</p>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Suggest two or three. They're a starting point: you agree the final list together in the meeting. Their suggestions are on the left.</p>
                    {editable && (
                      <JdObjectiveIdeas
                        jd={onlineJd(jobDescription)}
                        taken={mgrForm.objectives.map((o) => o.objective)}
                        onAdd={(objective, howMeasured) => {
                          const rows = mgrForm.objectives.filter((o) => o.objective.trim() || o.how_measured.trim());
                          setMgrForm({ ...mgrForm, objectives: [...rows, { objective, how_measured: howMeasured, timeframe: "" }] });
                        }}
                      />
                    )}
                    {mgrForm.objectives.map((row, idx) => (
                      <div key={idx} className="mb-4 rounded-lg border border-gray-100 bg-gray-50 p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-500">#{idx + 1}</span>
                          {mgrForm.objectives.length > 1 && editable && (
                            <button type="button" onClick={() => {
                              setMgrForm({ ...mgrForm, objectives: mgrForm.objectives.filter((_, i) => i !== idx) });
                            }} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                          )}
                        </div>
                        <textarea
                          rows={2}
                          disabled={!editable}
                          placeholder="Objective"
                          value={row.objective}
                          onChange={(e) => {
                            const updated = [...mgrForm.objectives];
                            updated[idx] = { ...row, objective: e.target.value };
                            setMgrForm({ ...mgrForm, objectives: updated });
                          }}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm mb-2 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-50"
                        />
                        <input
                          type="text"
                          disabled={!editable}
                          placeholder="How will this be measured?"
                          value={row.how_measured}
                          onChange={(e) => {
                            const updated = [...mgrForm.objectives];
                            updated[idx] = { ...row, how_measured: e.target.value };
                            setMgrForm({ ...mgrForm, objectives: updated });
                          }}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm mb-2 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-50"
                        />
                        <TimeframePicker
                          value={row.timeframe}
                          disabled={!editable}
                          onChange={(v) => {
                            const updated = [...mgrForm.objectives];
                            updated[idx] = { ...row, timeframe: v };
                            setMgrForm({ ...mgrForm, objectives: updated });
                          }}
                        />
                      </div>
                    ))}
                    {editable && (
                      <button type="button" onClick={() => {
                        setMgrForm({ ...mgrForm, objectives: [...mgrForm.objectives, { objective: "", how_measured: "", timeframe: "" }] });
                      }} className="mt-1 text-sm text-purple-600 hover:underline">+ Add Objective</button>
                    )}
                  </SectionCard>
                )}

                {/* Step 8: Development Plan */}
                {currentStep === 8 && (
                  <SectionCard title="Step 8: Development plan">
                    <p className="text-xs text-gray-500 mb-3">The help {mgrData.employee_name.split(" ")[0]} gets to grow: training, coaching, time or a project to learn from. Who arranges it, and by when. (What they'll deliver goes in objectives.)</p>
                    {mgrForm.development_plan.map((row, idx) => (
                      <div key={idx} className="mb-4 rounded-lg border border-gray-100 bg-gray-50 p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-500">#{idx + 1}</span>
                          {mgrForm.development_plan.length > 1 && editable && (
                            <button type="button" onClick={() => {
                              setMgrForm({ ...mgrForm, development_plan: mgrForm.development_plan.filter((_, i) => i !== idx) });
                            }} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                          )}
                        </div>
                        <textarea
                          rows={2}
                          disabled={!editable}
                          placeholder="e.g. Excel intermediate course, or shadowing the planning team"
                          value={row.support_needed}
                          onChange={(e) => {
                            const updated = [...mgrForm.development_plan];
                            updated[idx] = { ...row, support_needed: e.target.value };
                            setMgrForm({ ...mgrForm, development_plan: updated });
                          }}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm mb-2 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-50"
                        />
                        <input
                          type="text"
                          disabled={!editable}
                          placeholder="Who arranges it (e.g. you, HR, Training)"
                          value={row.provided_by || ""}
                          onChange={(e) => {
                            const updated = [...mgrForm.development_plan];
                            updated[idx] = { ...row, provided_by: e.target.value };
                            setMgrForm({ ...mgrForm, development_plan: updated });
                          }}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm mb-2 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-50"
                        />
                        <TimeframePicker
                          value={row.timeframe}
                          disabled={!editable}
                          onChange={(v) => {
                            const updated = [...mgrForm.development_plan];
                            updated[idx] = { ...row, timeframe: v };
                            setMgrForm({ ...mgrForm, development_plan: updated });
                          }}
                        />
                      </div>
                    ))}
                    {editable && (
                      <button type="button" onClick={() => {
                        setMgrForm({ ...mgrForm, development_plan: [...mgrForm.development_plan, { support_needed: "", provided_by: "", timeframe: "" }] });
                      }} className="mt-1 text-sm text-purple-600 hover:underline">+ Add another</button>
                    )}
                  </SectionCard>
                )}

                {/* Step 9: Your rating */}
                {currentStep === 9 && (
                  <SectionCard title="Step 9: Your rating">
                    <ManagerRatingGuide compact />
                    <div className="mb-5">
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Your overall rating for the year</label>
                      <RatingScale
                        size="lg"
                        name="Your overall rating"
                        disabled={!editable}
                        value={mgrForm.overall_manager_rating}
                        onChange={(n) => setMgrForm({ ...mgrForm, overall_manager_rating: n })}
                      />
                      <ExampleBox
                        rating={mgrForm.overall_manager_rating}
                        value={mgrForm.overall_example}
                        disabled={!editable}
                        subject="their year"
                        onChange={(text) => setMgrForm({ ...mgrForm, overall_example: text })}
                      />
                      {needsExample(mgrForm.overall_manager_rating) && !hasBackedHighRating(mgrForm) && (
                        <p className="mt-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
                          An overall {mgrForm.overall_manager_rating} also needs at least one responsibility or behaviour rated 4 or 5, with its example (steps 2 and 6).
                        </p>
                      )}
                    </div>

                    <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
                      <p className="font-semibold text-gray-800">This is your rating going into the meeting.</p>
                      <p className="mt-0.5">You agree the final rating together in the review meeting. The meeting guide shows both views side by side.</p>
                    </div>

                    {mgrData.overall_self_rating && (
                      <div className="rounded-lg bg-blue-50 border border-blue-100 p-3">
                        <p className="text-xs text-blue-700">Their own view of their year: <span className="font-bold">{selfRatingText(mgrData.overall_self_rating, mgrData.rating_words)}</span></p>
                        {mgrData.rating_words && (
                          <p className="mt-0.5 text-[11px] text-blue-600">Staff describe their own year in words, not on the company 1 to 5 scale, so the two don't line up number for number.</p>
                        )}
                      </div>
                    )}
                  </SectionCard>
                )}

                {/* Step 10: Catch-ups */}
                {currentStep === 10 && (
                  <SectionCard title="Step 10: Catch-ups after the appraisal">
                    <p className="text-xs text-gray-500 mb-3">
                      Short, regular catch-ups keep objectives moving between appraisals. Suggest how often; you agree it with
                      {" "}{mgrData.employee_name.split(" ")[0]} in the meeting.
                    </p>
                    <CheckInPicker
                      disabled={!editable}
                      value={{ cadence: mgrForm.check_in_cadence, minutes: mgrForm.check_in_minutes, time: mgrForm.check_in_time }}
                      onChange={(v) => setMgrForm({ ...mgrForm, check_in_cadence: v.cadence, check_in_minutes: v.minutes, check_in_time: v.time })}
                    />
                    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
                      <p className="font-semibold text-gray-800">What happens next</p>
                      <ul className="mt-1 space-y-0.5">
                        <li>• Once the appraisal is finished, we email you the catch-ups to add to your calendar.</li>
                        <li>• We remind you the day before each one.</li>
                        <li>• You can move, skip or stop them from My team.</li>
                      </ul>
                    </div>
                  </SectionCard>
                )}

                {/* Step 11: Risk & Succession (CONFIDENTIAL) */}
                {currentStep === 11 && (
                  <div>
                    <div className="mb-4 rounded-lg border-2 border-red-300 bg-red-50 p-3">
                      <p className="text-xs font-bold text-red-700 uppercase">CONFIDENTIAL — This section is not shared with the employee</p>
                    </div>
                    <SectionCard title="Step 11: Risk & succession">
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1 block">Flight Risk</label>
                          <select
                            disabled={!editable}
                            value={mgrForm.risk_flight_risk}
                            onChange={(e) => setMgrForm({ ...mgrForm, risk_flight_risk: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-50"
                          >
                            <option value="">Select...</option>
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                          </select>
                        </div>

                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1 block">Retention Priority</label>
                          <select
                            disabled={!editable}
                            value={mgrForm.risk_retention_priority}
                            onChange={(e) => setMgrForm({ ...mgrForm, risk_retention_priority: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-50"
                          >
                            <option value="">Select...</option>
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                          </select>
                        </div>

                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1 block">Successor</label>
                          <input
                            type="text"
                            disabled={!editable}
                            placeholder="Name of potential successor..."
                            value={mgrForm.risk_successor}
                            onChange={(e) => setMgrForm({ ...mgrForm, risk_successor: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-50"
                          />
                        </div>

                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            disabled={!editable}
                            checked={mgrForm.risk_single_point_of_failure}
                            onChange={(e) => setMgrForm({ ...mgrForm, risk_single_point_of_failure: e.target.checked })}
                            className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Single Point of Failure</span>
                        </label>

                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1 block">Risk Commentary</label>
                          <textarea
                            rows={4}
                            disabled={!editable}
                            placeholder="Additional risk commentary..."
                            value={mgrForm.risk_commentary}
                            onChange={(e) => setMgrForm({ ...mgrForm, risk_commentary: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-50"
                          />
                        </div>
                      </div>
                    </SectionCard>
                  </div>
                )}

                {/* Step 12: Check and submit */}
                {currentStep === 12 && (
                  <SectionCard title="Step 12: Check and submit">
                    <p className="text-xs text-gray-500 mb-3">
                      When you submit, you book the review meeting with {mgrData.employee_name.split(" ")[0]}. In the meeting you go
                      through it together and agree the final rating, the objectives and the catch-ups.
                    </p>
                    <ul className="mb-4 space-y-1 text-xs">
                      {MANAGER_STEP_LABELS.slice(0, 11).map((label, i) => (
                        <li key={label} className="flex items-center gap-1.5">
                          {mgrCompletedSteps.has(i + 1)
                            ? <Check size={13} strokeWidth={2.5} className="text-green-600" aria-hidden />
                            : <span className="inline-block h-3 w-3 rounded-full border border-gray-300" aria-hidden />}
                          <button type="button" onClick={() => goToStep(i + 1)} className={mgrCompletedSteps.has(i + 1) ? "text-gray-700 hover:underline" : "text-amber-700 hover:underline"}>
                            {i + 1}. {label}{mgrCompletedSteps.has(i + 1) ? "" : " (not saved yet)"}
                          </button>
                        </li>
                      ))}
                    </ul>

                    {/* Attach Documents */}
                    <div className="mb-4">
                      <h4 className="text-xs font-semibold text-gray-700 mb-1">Supporting Documents</h4>
                      <p className="text-[11px] text-gray-400 mb-2">Attach supporting files to this review (PDF, JPG, PNG, DOCX — max 10 MB each)</p>

                      {reviewDocs.length > 0 && (
                        <ul className="mb-2 space-y-1">
                          {reviewDocs.map((d) => (
                            <li key={d.id} className="flex items-center justify-between rounded border border-gray-200 bg-white px-3 py-1.5">
                              <div className="min-w-0">
                                <p className="truncate text-xs font-medium text-gray-800">{d.title || d.file_name}</p>
                                <p className="text-[10px] text-gray-400">{d.file_type?.toUpperCase()}{d.file_size_bytes ? ` · ${(d.file_size_bytes / 1024).toFixed(0)} KB` : ""}</p>
                              </div>
                              {editable && (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      await api.delete(`/api/reviews/${selectedReviewId}/documents/${d.id}`);
                                      setReviewDocs((prev) => prev.filter((x) => x.id !== d.id));
                                    } catch { /* ignore */ }
                                  }}
                                  className="ml-2 shrink-0 rounded border border-red-200 px-1.5 py-0.5 text-[10px] text-red-600 hover:bg-red-50"
                                >
                                  Remove
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}

                      {editable && (
                        <>
                          {docFiles.length > 0 && (
                            <ul className="mb-2 space-y-1">
                              {docFiles.map((f, idx) => (
                                <li key={idx} className="flex items-center justify-between rounded border border-gray-200 bg-amber-50 px-3 py-1.5">
                                  <div className="min-w-0">
                                    <p className="truncate text-xs font-medium text-gray-800">{f.name}</p>
                                    <p className="text-[10px] text-gray-400">{(f.size / 1024).toFixed(0)} KB · pending upload</p>
                                  </div>
                                  <button type="button" onClick={() => setDocFiles((prev) => prev.filter((_, i) => i !== idx))} className="ml-2 shrink-0 rounded border border-red-200 px-1.5 py-0.5 text-[10px] text-red-600 hover:bg-red-50">Remove</button>
                                </li>
                              ))}
                            </ul>
                          )}

                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => docFileRef.current?.click()}
                              className="rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                            >
                              + Add Files
                            </button>
                            <input
                              ref={docFileRef}
                              type="file"
                              multiple
                              accept=".pdf,.jpg,.jpeg,.png,.docx"
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files?.length) {
                                  const maxSize = 10 * 1024 * 1024;
                                  const allowed = ["application/pdf", "image/jpeg", "image/png", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
                                  const valid = Array.from(e.target.files).filter((f) => allowed.includes(f.type) && f.size <= maxSize);
                                  setDocFiles((prev) => [...prev, ...valid].slice(0, 10));
                                  e.target.value = "";
                                }
                              }}
                            />
                            {docFiles.length > 0 && (
                              <button
                                type="button"
                                disabled={docUploading}
                                onClick={async () => {
                                  setDocUploading(true);
                                  try {
                                    const cats = await api.get<{ id: number; name: string }[]>("/api/documents/categories");
                                    const perfCat = cats.find((c) => c.name === "Performance Reviews");
                                    const categoryId = perfCat?.id ?? cats[0]?.id ?? 1;
                                    for (const file of docFiles) {
                                      const form = new FormData();
                                      form.append("file", file);
                                      const params = new URLSearchParams({
                                        title: `${mgrData.cycle_name || "Review"} — ${file.name}`,
                                        category_id: String(categoryId),
                                        employee_id: String(mgrData.employee_id),
                                      });
                                      const res = await fetch(`/api/documents?${params.toString()}`, { method: "POST", credentials: "include", body: form });
                                      if (res.ok) {
                                        const doc = await res.json() as { id: number };
                                        await api.post(`/api/reviews/${selectedReviewId}/documents`, { document_id: doc.id });
                                      }
                                    }
                                    setDocFiles([]);
                                    const docs = await api.get<typeof reviewDocs>(`/api/reviews/${selectedReviewId}/documents`);
                                    setReviewDocs(docs);
                                  } catch { /* ignore */ }
                                  setDocUploading(false);
                                }}
                                className="rounded bg-purple-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-purple-700 disabled:opacity-50"
                              >
                                {docUploading ? "Uploading..." : `Upload ${docFiles.length} file${docFiles.length !== 1 ? "s" : ""}`}
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    {editable && (
                      <>
                        {colleagueFeedbackOutstanding && (
                          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                            <p className="text-xs font-semibold text-amber-800">Waiting on colleague feedback</p>
                            <p className="text-xs text-amber-700 mt-1">
                              Your review is saved and you can re-open it at any time, but it can't be
                              submitted until {pendingColleagues.length}{" "}
                              {pendingColleagues.length === 1 ? "reviewer has" : "reviewers have"} responded:{" "}
                              {pendingColleagues.map((c) => c.colleague_name).join(", ")}.
                            </p>
                            <p className="text-xs text-amber-700 mt-1">
                              To proceed without their input, use “Skip Colleague Feedback”.
                            </p>
                          </div>
                        )}
                        {managerExampleGaps(mgrForm).length > 0 && (
                          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                            <p className="text-xs font-semibold text-amber-800">A 4 or 5 needs an example</p>
                            <p className="text-xs text-amber-700 mt-1">
                              Add a specific example for: {managerExampleGaps(mgrForm).join("; ")}. Or change the rating to a 3 if it was a solid job.
                            </p>
                          </div>
                        )}
                        <button
                          type="button"
                          disabled={mgrSaving || !mgrForm.overall_manager_rating || colleagueFeedbackOutstanding || managerExampleGaps(mgrForm).length > 0}
                          onClick={() => setMgrShowConfirm(true)}
                          className="w-full rounded-lg bg-green-600 py-3 text-sm font-bold text-white hover:bg-green-700 disabled:opacity-50"
                        >
                          Submit and book the meeting
                        </button>
                      </>
                    )}
                    {!mgrForm.overall_manager_rating && editable && (
                      <p className="text-xs text-amber-600 mt-2 text-center">Set an overall manager rating (Step 9) before submitting.</p>
                    )}
                  </SectionCard>
                )}
              </div>
            </div>

            {/* Save messages */}
            {mgrSaveMsg && (
              <p className={`text-sm mt-3 ${mgrSaveMsg.includes("fail") || mgrSaveMsg.includes("Failed") ? "text-red-600" : "text-green-600"}`}>
                {mgrSaveMsg}
              </p>
            )}

            {/* Navigation buttons */}
            {editable && managerExampleGaps(mgrForm, currentStep).length > 0 && (
              <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Before you move on: add a specific example for {managerExampleGaps(mgrForm, currentStep).join("; ")}, or change the rating to a 3. You can still save a draft.
              </p>
            )}
            {editable && (
              <div className="flex gap-2 mt-4">
                {currentStep > 1 && (
                  <button
                    type="button"
                    onClick={() => goToStep(currentStep - 1)}
                    className="flex-1 rounded-lg bg-gray-100 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200"
                  >
                    Back
                  </button>
                )}
                <button
                  type="button"
                  disabled={mgrSaving}
                  onClick={() => saveManagerReview()}
                  className="rounded-lg bg-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-300 disabled:opacity-50"
                >
                  Save Draft
                </button>
                {currentStep < MANAGER_TOTAL_STEPS && (
                  <button
                    type="button"
                    disabled={mgrSaving || managerExampleGaps(mgrForm, currentStep).length > 0}
                    title={managerExampleGaps(mgrForm, currentStep).length > 0 ? "Add an example for each 4 or 5 first" : undefined}
                    onClick={() => saveManagerReview(currentStep + 1)}
                    className="flex-1 rounded-lg bg-purple-600 py-2.5 text-sm font-semibold text-white hover:bg-purple-700 disabled:opacity-50"
                  >
                    {mgrSaving ? "Saving..." : "Save & Continue"}
                  </button>
                )}
              </div>
            )}

            {/* Submit confirmation dialog */}
            {mgrShowConfirm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                <div className="mx-4 max-w-sm rounded-2xl bg-white p-6 shadow-xl">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Submit your review?</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    You won't be able to change it after this. Next you book the review meeting; {mgrData.employee_name.split(" ")[0]} doesn't
                    see your ratings until the meeting is finished.
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setMgrShowConfirm(false)}
                      className="flex-1 rounded-lg bg-gray-100 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={mgrSaving}
                      onClick={submitManagerReview}
                      className="flex-1 rounded-lg bg-green-600 py-2.5 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      {mgrSaving ? "Submitting..." : "Yes, submit"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </main>
          <BottomNav />
        </div>
      );
    }

    // ---------- Self-Assessment Wizard ----------

    if (showSelfAssessmentWizard) {
      const selfScale = reviewData.rating_words ? "self" : "company";
      const jdAvailable = hasJobDescription(jobDescription);
      return (
        <div className={`flex min-h-dvh flex-col bg-pme-paper safe-x ${jdOpen && jdAvailable ? "lg:pr-[min(46vw,760px)]" : ""}`}>
          {jdOpen && jdAvailable && <JobDescriptionPanel jd={jobDescription} onClose={() => setJdOpen(false)} />}
          <header className="safe-top px-5 pt-4 pb-3">
            <button
              type="button"
              onClick={() => setSearchParams({ review: String(selectedReviewId) }, { replace: true })}
              className="text-sm text-pme-blue mb-2"
            >
              &larr; Back to my appraisal
            </button>
            <p className="eyebrow">Self-assessment · {appraisalName(reviewData.appraisal_year)}</p>
            <h1 className="font-display text-3xl text-pme-navy">Your self-assessment</h1>
            {reviewData.self_assessment_deadline && (
              <p className="mt-0.5 text-xs text-gray-500">
                Please finish by {formatDate(reviewData.self_assessment_deadline, "long")} · {dueText(reviewData.self_assessment_deadline)}
              </p>
            )}
            {jdAvailable && (
              <button
                type="button"
                onClick={() => setJdOpen((o) => !o)}
                aria-pressed={jdOpen}
                className={`mt-2 inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold ${
                  jdOpen ? "border-pme-navy bg-pme-navy text-white" : "border-pme-blue/30 bg-white text-pme-blue hover:bg-pme-sky"
                }`}
              >
                <FileText size={14} aria-hidden />
                {jdOpen ? "Hide my job description" : "Open my job description"}
              </button>
            )}
          </header>

          <main className="mx-auto w-full max-w-2xl flex-1 overflow-y-auto px-5 py-4 pb-24 space-y-4">
            <ProgressBar step={currentStep} completed={completedSteps.size} />
            <StepNav step={currentStep} onStep={(n) => void saveSelfAssessment(n)} completedSteps={completedSteps} />

            {currentStep === 1 && completedSteps.size === 0 && !introDismissed && (
              <div className="rounded-xl border border-pme-blue/20 bg-white p-4 shadow-sm">
                <p className="text-sm font-semibold text-pme-navy">Before you start</p>
                <ul className="mt-2 space-y-1.5 text-sm text-gray-600">
                  <li className="flex gap-2"><Clock size={15} aria-hidden className="mt-0.5 shrink-0 text-pme-blue" />It takes about 30 to 45 minutes. {jdAvailable ? "Your job description opens beside the form, so you can check it as you go." : "Have your job description to hand."}</li>
                  <li className="flex gap-2"><Save size={15} aria-hidden className="mt-0.5 shrink-0 text-pme-blue" />Each step saves when you move on, so you can stop and come back at any time.</li>
                  <li className="flex gap-2"><Eye size={15} aria-hidden className="mt-0.5 shrink-0 text-pme-blue" />{reviewData.manager_name ? `${reviewData.manager_name} sees it once you submit.` : "Your manager sees it once you submit."} Nobody else does.</li>
                </ul>
                <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-gray-500">The 9 sections</p>
                <ol className="mt-1 grid gap-x-4 gap-y-0.5 text-sm text-gray-600 sm:grid-cols-2">
                  {selfAssessmentSteps().map((st) => (
                    <li key={st.n} className="flex gap-1.5"><span className="w-4 shrink-0 font-semibold text-pme-blue">{st.n}</span>{st.title}</li>
                  ))}
                </ol>
                <p className="mt-1.5 text-xs text-gray-500">You can jump between sections using the buttons above, and fill them in any order.</p>
                {reviewData.rating_words && (
                  <p className="mt-2 text-xs text-gray-500">
                    For each part of your job you'll choose how you think it went: Needs support, Getting there, Going well or A real strength. It's your own view, not a score.
                  </p>
                )}
                <button type="button" onClick={() => setIntroDismissed(true)} className="mt-3 text-xs font-semibold text-pme-blue hover:underline">
                  Got it
                </button>
              </div>
            )}

            {/* Step 1: Key Responsibilities */}
            {currentStep === 1 && (
              <SectionCard title={selfAssessmentSteps()[0].title}>
                <StepGuide n={1} />
                {jdAvailable && !jdOpen && (
                  <button
                    type="button"
                    onClick={() => setJdOpen(true)}
                    className="mb-3 flex w-full items-center gap-3 rounded-lg border border-pme-blue/20 bg-pme-sky/60 p-3 text-left hover:bg-pme-sky"
                  >
                    <FileText size={20} aria-hidden className="shrink-0 text-pme-blue" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-pme-navy">Open your job description beside this form</span>
                      <span className="block text-xs text-gray-600">{onlineJd(jobDescription) ? "Your key result areas are already the rows below." : "Use its key responsibilities as the rows below."}</span>
                    </span>
                  </button>
                )}
                {!jdAvailable && jobDescription && (
                  <p className="mb-3 text-xs text-gray-500">There's no job description on your record yet. Ask HR if you'd like a copy.</p>
                )}
                {formState.responsibilities.map((row, idx) => (
                  <div key={idx} className="mb-4 rounded-lg border border-gray-100 bg-gray-50 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-500">#{idx + 1}</span>
                      {formState.responsibilities.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const updated = formState.responsibilities.filter((_, i) => i !== idx);
                            setFormState({ ...formState, responsibilities: updated });
                          }}
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      placeholder="Responsibility area"
                      value={row.responsibility_area}
                      onChange={(e) => {
                        const updated = [...formState.responsibilities];
                        updated[idx] = { ...row, responsibility_area: e.target.value };
                        setFormState({ ...formState, responsibilities: updated });
                      }}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm mb-2 focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
                    />
                    <JdAreaHint kra={findKra(onlineJd(jobDescription), row.responsibility_area)} />
                    <textarea
                      rows={3}
                      placeholder="Your summary of performance in this area"
                      value={row.employee_summary}
                      onChange={(e) => {
                        const updated = [...formState.responsibilities];
                        updated[idx] = { ...row, employee_summary: e.target.value };
                        setFormState({ ...formState, responsibilities: updated });
                      }}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm mb-2 focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
                    />
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">{reviewData.rating_words ? "How do you think it went?" : "Self Rating"}</label>
                      <RatingInput
                        scale={selfScale}
                        value={row.self_rating}
                        onChange={(v) => {
                          const updated = [...formState.responsibilities];
                          updated[idx] = { ...row, self_rating: v };
                          setFormState({ ...formState, responsibilities: updated });
                        }}
                      />
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setFormState({
                      ...formState,
                      responsibilities: [...formState.responsibilities, { responsibility_area: "", employee_summary: "", self_rating: null }],
                    });
                  }}
                  className="mt-1 text-sm text-pme-blue hover:underline"
                >
                  + Add Responsibility
                </button>
              </SectionCard>
            )}

            {/* Step 2: Strengths */}
            {currentStep === 2 && (
              <SectionCard title={selfAssessmentSteps()[1].title}>
                <StepGuide n={2} />
                {formState.strengths.map((row, idx) => (
                  <div key={idx} className="mb-4 rounded-lg border border-gray-100 bg-gray-50 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-500">#{idx + 1}</span>
                      {formState.strengths.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const updated = formState.strengths.filter((_, i) => i !== idx);
                            setFormState({ ...formState, strengths: updated });
                          }}
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      placeholder="Strength"
                      value={row.strength}
                      onChange={(e) => {
                        const updated = [...formState.strengths];
                        updated[idx] = { ...row, strength: e.target.value };
                        setFormState({ ...formState, strengths: updated });
                      }}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm mb-2 focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
                    />
                    <textarea
                      rows={3}
                      placeholder="Example or evidence that this is still valid"
                      value={row.still_valid_example}
                      onChange={(e) => {
                        const updated = [...formState.strengths];
                        updated[idx] = { ...row, still_valid_example: e.target.value };
                        setFormState({ ...formState, strengths: updated });
                      }}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setFormState({
                      ...formState,
                      strengths: [...formState.strengths, { strength: "", still_valid_example: "" }],
                    });
                  }}
                  className="mt-1 text-sm text-pme-blue hover:underline"
                >
                  + Add Strength
                </button>
              </SectionCard>
            )}

            {/* Step 3: Development Areas */}
            {currentStep === 3 && (
              <SectionCard title={selfAssessmentSteps()[2].title}>
                <StepGuide n={3} />
                {formState.development_areas.map((row, idx) => (
                  <div key={idx} className="mb-4 rounded-lg border border-gray-100 bg-gray-50 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-500">#{idx + 1}</span>
                      {formState.development_areas.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const updated = formState.development_areas.filter((_, i) => i !== idx);
                            setFormState({ ...formState, development_areas: updated });
                          }}
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      placeholder="Development area"
                      value={row.area}
                      onChange={(e) => {
                        const updated = [...formState.development_areas];
                        updated[idx] = { ...row, area: e.target.value };
                        setFormState({ ...formState, development_areas: updated });
                      }}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm mb-2 focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
                    />
                    <textarea
                      rows={3}
                      placeholder="What progress have you made?"
                      value={row.employee_progress}
                      onChange={(e) => {
                        const updated = [...formState.development_areas];
                        updated[idx] = { ...row, employee_progress: e.target.value };
                        setFormState({ ...formState, development_areas: updated });
                      }}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm mb-2 focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
                    />
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">{reviewData.rating_words ? "How do you think it went?" : "Self Rating"}</label>
                      <RatingInput
                        scale={selfScale}
                        value={row.self_rating}
                        onChange={(v) => {
                          const updated = [...formState.development_areas];
                          updated[idx] = { ...row, self_rating: v };
                          setFormState({ ...formState, development_areas: updated });
                        }}
                      />
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setFormState({
                      ...formState,
                      development_areas: [...formState.development_areas, { area: "", employee_progress: "", self_rating: null }],
                    });
                  }}
                  className="mt-1 text-sm text-pme-blue hover:underline"
                >
                  + Add Development Area
                </button>
              </SectionCard>
            )}

            {/* Step 4: Could Do Better */}
            {currentStep === 4 && (
              <SectionCard title={selfAssessmentSteps()[3].title}>
                <StepGuide n={4} />
                <textarea
                  rows={6}
                  placeholder="What could you do better in your role?"
                  value={formState.could_do_better}
                  onChange={(e) => setFormState({ ...formState, could_do_better: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
                />
              </SectionCard>
            )}

            {/* Step 5: Behaviours */}
            {currentStep === 5 && (
              <SectionCard title={selfAssessmentSteps()[4].title}>
                <StepGuide n={5} />
                {formState.behaviours.map((row, idx) => (
                  <div key={idx} className="mb-4 rounded-lg border border-gray-100 bg-gray-50 p-3">
                    <p className="text-sm font-medium text-gray-800 mb-2">{row.behaviour_name}</p>
                    <JdCompetencyHint competency={findCompetency(onlineJd(jobDescription), row.behaviour_name)} />
                    <textarea
                      rows={3}
                      placeholder="Provide an example of how you demonstrated this behaviour"
                      value={row.employee_example}
                      onChange={(e) => {
                        const updated = [...formState.behaviours];
                        updated[idx] = { ...row, employee_example: e.target.value };
                        setFormState({ ...formState, behaviours: updated });
                      }}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm mb-2 focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
                    />
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">{reviewData.rating_words ? "How do you think you do?" : "Rating"}</label>
                      <RatingInput
                        scale={selfScale}
                        value={row.employee_rating}
                        onChange={(v) => {
                          const updated = [...formState.behaviours];
                          updated[idx] = { ...row, employee_rating: v };
                          setFormState({ ...formState, behaviours: updated });
                        }}
                      />
                    </div>
                  </div>
                ))}
              </SectionCard>
            )}

            {/* Step 6: Objectives */}
            {currentStep === 6 && (
              <SectionCard title={selfAssessmentSteps()[5].title}>
                <ObjectivesHighlight own />
                <StepGuide n={6} />
                {formState.objectives.map((row, idx) => (
                  <div key={idx} className="mb-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-500">#{idx + 1}</span>
                      {formState.objectives.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const updated = formState.objectives.filter((_, i) => i !== idx);
                            setFormState({ ...formState, objectives: updated });
                          }}
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <textarea
                      rows={3}
                      placeholder="Describe the objective"
                      value={row.objective}
                      onChange={(e) => {
                        const updated = [...formState.objectives];
                        updated[idx] = { ...row, objective: e.target.value };
                        setFormState({ ...formState, objectives: updated });
                      }}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setFormState({
                      ...formState,
                      objectives: [...formState.objectives, { objective: "" }],
                    });
                  }}
                  className="mt-1 text-sm text-pme-blue hover:underline"
                >
                  + Add Objective
                </button>
              </SectionCard>
            )}

            {/* Step 7: Additional Comments */}
            {currentStep === 7 && (
              <SectionCard title={selfAssessmentSteps()[6].title}>
                <StepGuide n={7} />
                <textarea
                  rows={6}
                  placeholder="Additional comments..."
                  value={formState.additional_comments}
                  onChange={(e) => setFormState({ ...formState, additional_comments: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
                />
              </SectionCard>
            )}

            {/* Step 8: Role & Aspirations */}
            {currentStep === 8 && (
              <SectionCard title={selfAssessmentSteps()[7].title}>
                <StepGuide n={8} />
                <textarea
                  rows={6}
                  placeholder="Your role aspirations and career goals..."
                  value={formState.role_aspirations}
                  onChange={(e) => setFormState({ ...formState, role_aspirations: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
                />
              </SectionCard>
            )}

            {/* Step 9: Overall Rating & Submit */}
            {currentStep === 9 && (
              <SectionCard title={selfAssessmentSteps()[8].title}>
                <StepGuide n={9} />

                <div className="mb-6">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Overall, how did your year go?</label>
                  <RatingScale
                    size="lg"
                    name="Overall self-rating"
                    scale={selfScale}
                    value={formState.overall_self_rating}
                    onChange={(n) => setFormState({ ...formState, overall_self_rating: n })}
                  />
                </div>

                <div className="mb-4">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Top Priority 1</label>
                  <input
                    type="text"
                    placeholder="Your top priority for the next period"
                    value={formState.top_priority_1}
                    onChange={(e) => setFormState({ ...formState, top_priority_1: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
                  />
                </div>

                <div className="mb-6">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Top Priority 2</label>
                  <input
                    type="text"
                    placeholder="Your second priority for the next period"
                    value={formState.top_priority_2}
                    onChange={(e) => setFormState({ ...formState, top_priority_2: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
                  />
                </div>

                {selfAssessmentGaps.length > 0 && (
                  <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                    <p className="font-semibold">Steps you haven't filled in yet</p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {selfAssessmentGaps.map((g) => (
                        <button
                          key={g.n}
                          type="button"
                          onClick={() => void saveSelfAssessment(g.n)}
                          className="rounded-full border border-amber-300 bg-white px-2 py-0.5 font-semibold text-amber-800 hover:bg-amber-100"
                        >
                          {g.n}. {g.short}
                        </button>
                      ))}
                    </div>
                    <p className="mt-1.5">You can still submit, but your manager will see these as blank.</p>
                  </div>
                )}
                <button
                  type="button"
                  disabled={saving || !formState.overall_self_rating}
                  onClick={() => setShowConfirm(true)}
                  className="w-full rounded-lg bg-green-600 py-3 text-sm font-bold text-white hover:bg-green-700 disabled:opacity-50"
                >
                  Submit my self-assessment
                </button>
                {!formState.overall_self_rating && (
                  <p className="mt-1.5 text-center text-xs text-gray-500">Choose an overall rating to submit.</p>
                )}
              </SectionCard>
            )}

            {/* Save & Continue / Back buttons */}
            {saveMsg && (
              <p className={`text-sm ${saveMsg.includes("fail") || saveMsg.includes("Failed") ? "text-red-600" : "text-green-600"}`}>
                {saveMsg}
              </p>
            )}

            <div className="flex gap-2">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={() => goToStep(currentStep - 1)}
                  className="flex-1 rounded-lg bg-gray-100 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200"
                >
                  Back
                </button>
              )}
              {currentStep < TOTAL_STEPS && (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => saveSelfAssessment(currentStep + 1)}
                  className="flex-1 rounded-lg bg-pme-blue py-2.5 text-sm font-semibold text-white hover:bg-pme-blue/90 disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save & Continue"}
                </button>
              )}
              {currentStep === TOTAL_STEPS && (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => saveSelfAssessment()}
                  className="flex-1 rounded-lg border border-gray-300 bg-white py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save for later"}
                </button>
              )}
            </div>

            {/* Confirm dialog */}
            {showConfirm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                <div className="mx-4 max-w-sm rounded-2xl bg-white p-6 shadow-xl">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Send your self-assessment?</h3>
                  <p className="text-sm text-gray-600">
                    It goes to {reviewData.manager_name || "your manager"}, and you won't be able to change it afterwards.
                  </p>
                  <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-gray-500">What happens next</p>
                  <ul className="mt-1 mb-4 space-y-1 text-sm text-gray-600">
                    <li>Your manager may ask a few colleagues for feedback.</li>
                    <li>{reviewData.manager_name || "Your manager"} writes their review.</li>
                    <li>You meet to agree a final rating and your objectives.</li>
                  </ul>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowConfirm(false)}
                      className="flex-1 rounded-lg bg-gray-100 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={submitSelfAssessment}
                      className="flex-1 rounded-lg bg-green-600 py-2.5 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      {saving ? "Sending..." : "Yes, send it"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </main>
          <BottomNav />
        </div>
      );
    }

    // ---------- Historical Review Detail View ----------

    if (reviewData.is_historical) {
      return (
        <div className="flex min-h-dvh flex-col bg-white safe-x">
          <header className="safe-top px-5 pt-4 pb-3">
            <button type="button" onClick={goToList} className="text-sm text-pme-blue mb-2">&larr; Back to appraisals</button>
            {renderReleaseFeedbackBar()}
            {renderEmployeePreviewBar()}
            <h1 className="text-xl font-semibold text-gray-900">{reviewData.employee_name} — Historical Review</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="inline-block rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">Completed</span>
              <span className="inline-block rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-700">Historical</span>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto px-5 py-4 pb-24 space-y-4">
            {/* Review Info */}
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Review Details</h3>
              <div className="space-y-1 text-sm text-gray-600">
                {reviewData.review_date && (
                  <p>Review Date: <span className="font-medium">{DATE_FMT.format(new Date(reviewData.review_date))}</span></p>
                )}
                {(reviewData.review_period_start || reviewData.review_period_end) && (
                  <p>Period: <span className="font-medium">
                    {reviewData.review_period_start ? DATE_FMT.format(new Date(reviewData.review_period_start)) : "–"}
                    {" — "}
                    {reviewData.review_period_end ? DATE_FMT.format(new Date(reviewData.review_period_end)) : "–"}
                  </span></p>
                )}
                {reviewData.manager_name && (
                  <p>Reviewer: <span className="font-medium">{reviewData.manager_name}</span></p>
                )}
              </div>
            </div>

            {/* Ratings */}
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-xl border border-gray-200 p-3 text-center">
                <p className={`font-bold text-pme-blue ${reviewData.rating_words ? "text-base leading-8" : "text-2xl"}`}>{reviewData.rating_words ? selfRatingText(reviewData.overall_self_rating, true) : reviewData.overall_self_rating != null ? reviewData.overall_self_rating : "–"}</p>
                <p className="text-xs text-gray-500">{reviewData.rating_words ? "Your own view" : "Self Rating"}</p>
              </div>
              <div className="rounded-xl border border-gray-200 p-3 text-center">
                <p className="text-2xl font-bold text-purple-600">{reviewData.overall_manager_rating != null ? reviewData.overall_manager_rating : "–"}</p>
                <p className="text-xs text-gray-500">Manager Rating</p>
              </div>
              <div className="rounded-xl border border-gray-200 p-3 text-center">
                <p className="text-2xl font-bold text-green-600">{reviewData.overall_agreed_rating != null ? reviewData.overall_agreed_rating : "–"}</p>
                <p className="text-xs text-gray-500">Agreed Rating</p>
              </div>
            </div>

            {/* Summary */}
            {reviewData.historical_summary && (
              <SectionCard title="Summary">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{reviewData.historical_summary}</p>
              </SectionCard>
            )}

            {/* Objectives */}
            {reviewData.objectives.length > 0 && (
              <SectionCard title="Objectives">
                {reviewData.objectives.map((o, idx) => (
                  <div key={idx} className="mb-3 border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                    <p className="text-sm text-gray-700">{o.objective}</p>
                    {o.how_measured && <p className="text-xs text-gray-500 mt-0.5">Measured by: {o.how_measured}</p>}
                    {o.due_date && <p className="text-xs text-gray-500">Due: {DATE_FMT.format(new Date(o.due_date))}</p>}
                  </div>
                ))}
              </SectionCard>
            )}

            {/* Actions */}
            {(reviewData.action_recommendations?.length ?? 0) > 0 && (
              <SectionCard title="Actions">
                {(reviewData.action_recommendations ?? []).map((a, idx) => (
                  <div key={idx} className="mb-3 border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                    <div className="flex items-center gap-2">
                      <span className={`inline-block h-2 w-2 rounded-full ${a.recommended ? "bg-green-500" : "bg-gray-300"}`} />
                      <p className="text-sm font-medium text-gray-800">
                        {a.action_type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                      </p>
                    </div>
                    {a.commentary && <p className="text-xs text-gray-600 mt-1 ml-4">{a.commentary}</p>}
                  </div>
                ))}
              </SectionCard>
            )}

            {/* Documents */}
            {reviewData.documents && reviewData.documents.length > 0 && (
              <SectionCard title="Supporting Documents">
                <ul className="space-y-1">
                  {reviewData.documents.map((d) => (
                    <li key={d.id} className="flex items-center gap-2 text-sm text-gray-700">
                      <FileText size={15} strokeWidth={1.75} aria-hidden className="shrink-0 text-gray-400" />
                      <span>{d.label || d.file_name || `Document #${d.document_id}`}</span>
                    </li>
                  ))}
                </ul>
              </SectionCard>
            )}
          </main>
          <BottomNav />
        </div>
      );
    }

    // ---------- Completed / Read-Only Review View ----------

    // Before the meeting (manager_review_submitted / meeting_pending) everyone
    // lands on the review's landing page: book the meeting, open the guide.
    // The full read-only review is one click away (tab=view).
    const isReadOnlyView = reviewData
      ? ["completed", "employee_response_pending"].includes(reviewData.status)
        || (activeTab === "view" && ["meeting_pending", "manager_review_submitted"].includes(reviewData.status))
      : false;

    if (isReadOnlyView) {
      const canEditResponse = reviewData.status === "employee_response_pending" || reviewData.status === "completed";
      const readOnlyStatusMsg = STATUS_LABELS[reviewData.status]?.label;
      const isNotCompleted = reviewData.status !== "completed";

      return (
        <div className="flex min-h-dvh flex-col bg-white safe-x">
          <header className="safe-top px-5 pt-4 pb-3">
            {activeTab === "view" ? (
              <button type="button" onClick={() => setSearchParams({ review: String(selectedReviewId) }, { replace: true })} className="text-sm text-pme-blue mb-2">&larr; Back to the review meeting</button>
            ) : (
              <button type="button" onClick={goToList} className="text-sm text-pme-blue mb-2">&larr; {fromEmployee ? "Back to Employee" : "Back to appraisals"}</button>
            )}
            {renderReleaseFeedbackBar()}
            {renderEmployeePreviewBar()}
            <h1 className="text-xl font-semibold text-gray-900">{reviewData.cycle_name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <StatusBadge status={reviewData.status} />
              {reviewData.overall_agreed_rating != null && (
                <span className="text-xs text-gray-500">Agreed Rating: {reviewData.overall_agreed_rating}/5</span>
              )}
            </div>
            {reviewData.recommendation && (
              <span className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${
                reviewData.recommendation === "promote" ? "bg-green-100 text-green-700"
                : reviewData.recommendation === "pip" ? "bg-red-100 text-red-700"
                : "bg-gray-100 text-gray-700"
              }`}>
                {reviewData.recommendation.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
              </span>
            )}
          </header>

          <main className="flex-1 overflow-y-auto px-5 py-4 pb-24 space-y-4">
            {/* Status banner for non-completed reviews */}
            {isNotCompleted && !adminUnlocked && readOnlyStatusMsg && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm text-amber-800 font-medium">{readOnlyStatusMsg}</p>
                <p className="text-xs text-amber-600 mt-1">Your review data shown below is read-only. Full results will be available once the review is completed.</p>
              </div>
            )}
            {/* Deadlines */}
            {(reviewData.self_assessment_deadline || reviewData.colleague_feedback_deadline || reviewData.manager_review_deadline) && (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-1">Deadlines</h3>
                <div className="space-y-0.5 text-xs text-gray-600">
                  {reviewData.self_assessment_deadline && (
                    <p>Self-Assessment: {DATE_FMT.format(new Date(reviewData.self_assessment_deadline))}</p>
                  )}
                  {reviewData.colleague_feedback_deadline && (
                    <p>Colleague Feedback: {DATE_FMT.format(new Date(reviewData.colleague_feedback_deadline))}</p>
                  )}
                  {reviewData.manager_review_deadline && (
                    <p>Manager Review: {DATE_FMT.format(new Date(reviewData.manager_review_deadline))}</p>
                  )}
                </div>
              </div>
            )}

            {/* The summary written at the end of the review meeting */}
            {reviewData.status === "completed" && reviewData.meeting_notes && (
              <div className="rounded-xl border border-green-200 bg-green-50/60 p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-green-800">From the review meeting</h3>
                <p className="mt-1.5 whitespace-pre-wrap text-sm text-gray-800">{reviewData.meeting_notes}</p>
              </div>
            )}

            {/* Ratings Summary — a rating the server redacted has its KEY absent,
                so omit the card entirely rather than showing a "–" placeholder
                that implies hidden content. A key present but null is a genuine
                "not yet rated" and still shows "–". */}
            <div className={`grid gap-2 ${
              [true, "overall_manager_rating" in reviewData, "overall_agreed_rating" in reviewData]
                .filter(Boolean).length === 3 ? "grid-cols-3"
                : [true, "overall_manager_rating" in reviewData, "overall_agreed_rating" in reviewData]
                  .filter(Boolean).length === 2 ? "grid-cols-2" : "grid-cols-1"
            }`}>
              <div className="rounded-xl border border-gray-200 p-3 text-center">
                <p className={`font-bold text-pme-blue ${reviewData.rating_words ? "text-base leading-8" : "text-2xl"}`}>{reviewData.rating_words ? selfRatingText(reviewData.overall_self_rating, true) : reviewData.overall_self_rating != null ? reviewData.overall_self_rating : "–"}</p>
                <p className="text-xs text-gray-500">{reviewData.rating_words ? "Your own view" : "Self Rating"}</p>
              </div>
              {"overall_manager_rating" in reviewData && (
              <div className="rounded-xl border border-gray-200 p-3 text-center">
                <p className="text-2xl font-bold text-purple-600">{reviewData.overall_manager_rating != null ? reviewData.overall_manager_rating : "–"}</p>
                <p className="text-xs text-gray-500">Manager Rating</p>
              </div>
              )}
              {"overall_agreed_rating" in reviewData && (
              <div className="rounded-xl border border-gray-200 p-3 text-center">
                <p className="text-2xl font-bold text-green-600">{reviewData.overall_agreed_rating != null ? reviewData.overall_agreed_rating : "–"}</p>
                <p className="text-xs text-gray-500">Agreed Rating</p>
              </div>
              )}
            </div>

            {/* Responsibilities */}
            {reviewData.responsibilities.length > 0 && (
              <SectionCard title="Key Responsibilities">
                {reviewData.responsibilities.map((r, idx) => (
                  <div key={idx} className="mb-3 border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                    <p className="text-sm font-medium text-gray-800">{r.responsibility_area}</p>
                    {r.employee_summary && <p className="text-xs text-gray-600 mt-1">{r.employee_summary}</p>}
                    <div className="flex gap-4 mt-1">
                      {r.self_rating != null && <span className="text-xs text-gray-500">Self: {selfRatingText(r.self_rating, reviewData.rating_words)}</span>}
                      {r.manager_rating && <span className="text-xs text-gray-500">Manager: {r.manager_rating}/5</span>}
                    </div>
                  </div>
                ))}
              </SectionCard>
            )}

            {/* Strengths */}
            {reviewData.strengths.length > 0 && (
              <SectionCard title="Strengths">
                {reviewData.strengths.map((s, idx) => (
                  <div key={idx} className="mb-3 border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                    <p className="text-sm font-medium text-gray-800">{s.strength}</p>
                    {s.still_valid_example && <p className="text-xs text-gray-600 mt-1">{s.still_valid_example}</p>}
                  </div>
                ))}
              </SectionCard>
            )}

            {/* Development Areas */}
            {reviewData.development_areas.length > 0 && (
              <SectionCard title="Development Areas">
                {reviewData.development_areas.map((d, idx) => (
                  <div key={idx} className="mb-3 border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                    <p className="text-sm font-medium text-gray-800">{d.area}</p>
                    {d.employee_progress && <p className="text-xs text-gray-600 mt-1">{d.employee_progress}</p>}
                    {d.self_rating != null && <span className="text-xs text-gray-500">Self: {selfRatingText(d.self_rating, reviewData.rating_words)}</span>}
                  </div>
                ))}
              </SectionCard>
            )}

            {/* Could Do Better */}
            {reviewData.could_do_better && (
              <SectionCard title="Could Do Better">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{reviewData.could_do_better}</p>
              </SectionCard>
            )}

            {/* Behaviours */}
            {reviewData.behaviours.length > 0 && (
              <SectionCard title="Behaviours">
                {reviewData.behaviours.map((b, idx) => (
                  <div key={idx} className="mb-3 border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                    <p className="text-sm font-medium text-gray-800">{b.behaviour_name}</p>
                    {b.employee_example && <p className="text-xs text-gray-600 mt-1">{b.employee_example}</p>}
                    <div className="flex gap-4 mt-1">
                      {b.employee_rating != null && <span className="text-xs text-gray-500">Self: {selfRatingText(b.employee_rating, reviewData.rating_words)}</span>}
                      {b.manager_rating && <span className="text-xs text-gray-500">Manager: {b.manager_rating}/5</span>}
                    </div>
                  </div>
                ))}
              </SectionCard>
            )}

            {/* Objectives */}
            {reviewData.objectives.length > 0 && (
              <SectionCard title="Objectives">
                {reviewData.objectives.map((o, idx) => (
                  <div key={idx} className="mb-3 border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                    <p className="text-sm text-gray-700">{o.objective}</p>
                    {o.how_measured && <p className="text-xs text-gray-500 mt-0.5">Measured by: {o.how_measured}</p>}
                    {o.due_date && <p className="text-xs text-gray-500">Due: {DATE_FMT.format(new Date(o.due_date))}</p>}
                  </div>
                ))}
              </SectionCard>
            )}

            {/* Development Plan */}
            {(reviewData.development_plan?.length ?? 0) > 0 && (
              <SectionCard title="Development Plan">
                {(reviewData.development_plan ?? []).map((p, idx) => (
                  <div key={idx} className="mb-3 border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                    <p className="text-sm text-gray-700">{p.support_needed}</p>
                    {p.provided_by && <p className="text-xs text-gray-500 mt-0.5">Provided by: {p.provided_by}</p>}
                    {p.target_date && <p className="text-xs text-gray-500">Target: {DATE_FMT.format(new Date(p.target_date))}</p>}
                  </div>
                ))}
              </SectionCard>
            )}

            {/* Additional Comments */}
            {reviewData.additional_comments && (
              <SectionCard title="Additional Comments">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{reviewData.additional_comments}</p>
              </SectionCard>
            )}

            {/* Role Aspirations */}
            {reviewData.role_aspirations && (
              <SectionCard title="Role & Aspirations">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{reviewData.role_aspirations}</p>
              </SectionCard>
            )}

            {/* Top Priorities */}
            {(reviewData.top_priority_1 || reviewData.top_priority_2) && (
              <SectionCard title="Top Priorities">
                {reviewData.top_priority_1 && (
                  <div className="mb-2">
                    <span className="text-xs font-medium text-gray-500">Priority 1:</span>
                    <p className="text-sm text-gray-700">{reviewData.top_priority_1}</p>
                  </div>
                )}
                {reviewData.top_priority_2 && (
                  <div>
                    <span className="text-xs font-medium text-gray-500">Priority 2:</span>
                    <p className="text-sm text-gray-700">{reviewData.top_priority_2}</p>
                  </div>
                )}
              </SectionCard>
            )}

            {/* Feedback Themes */}
            {(reviewData.feedback_themes?.length ?? 0) > 0 && (
              <SectionCard title="Feedback Themes">
                {(reviewData.feedback_themes ?? []).map((t, idx) => (
                  <div key={idx} className="mb-3 border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                    <p className="text-sm font-medium text-gray-800">{t.theme}</p>
                    {t.reviewers_raising_it && <p className="text-xs text-gray-500 mt-0.5">Raised by: {t.reviewers_raising_it}</p>}
                    {t.manager_view && <p className="text-xs text-gray-600 mt-0.5">{t.manager_view}</p>}
                  </div>
                ))}
              </SectionCard>
            )}

            {/* Signatures */}
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Signatures</h3>
              <div className="space-y-1 text-xs">
                <p className={reviewData.signed_by_employee ? "text-green-600" : "text-gray-400"}>
                  {reviewData.signed_by_employee ? "Signed by Employee" : "Awaiting Employee Signature"}
                </p>
                <p className={reviewData.signed_by_manager ? "text-green-600" : "text-gray-400"}>
                  {reviewData.signed_by_manager ? "Signed by Manager" : "Awaiting Manager Signature"}
                </p>
                <p className={reviewData.signed_by_hr ? "text-green-600" : "text-gray-400"}>
                  {reviewData.signed_by_hr ? "Signed by HR" : "Awaiting HR Signature"}
                </p>
              </div>
              {reviewData.meeting_date && (
                <p className="text-xs text-gray-500 mt-2">Meeting Date: {DATE_FMT.format(new Date(reviewData.meeting_date))}</p>
              )}
            </div>

            {/* Employee Response Section */}
            <SectionCard title="Employee Response">
              {canEditResponse && !responseSaved ? (
                <>
                  <textarea
                    rows={5}
                    placeholder="Write your response to the review..."
                    value={employeeResponse}
                    onChange={(e) => setEmployeeResponse(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm mb-2 focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
                  />
                  {saveMsg && (
                    <p className={`text-xs mb-2 ${saveMsg.includes("fail") || saveMsg.includes("Failed") ? "text-red-600" : "text-green-600"}`}>
                      {saveMsg}
                    </p>
                  )}
                  <button
                    type="button"
                    disabled={responseSaving || !employeeResponse.trim()}
                    onClick={saveEmployeeResponse}
                    className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white hover:bg-pme-blue/90 disabled:opacity-50"
                  >
                    {responseSaving ? "Saving..." : "Submit Response"}
                  </button>
                </>
              ) : canEditResponse && responseSaved ? (
                <>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap mb-2">{employeeResponse}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-green-600 font-medium">Response submitted</span>
                    <button
                      type="button"
                      onClick={() => setResponseSaved(false)}
                      className="text-xs text-pme-blue hover:underline"
                    >
                      Edit
                    </button>
                  </div>
                  {saveMsg && (
                    <p className={`text-xs mt-1 ${saveMsg.includes("fail") || saveMsg.includes("Failed") ? "text-red-600" : "text-green-600"}`}>
                      {saveMsg}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {reviewData.employee_response || "No response yet."}
                </p>
              )}
            </SectionCard>
          </main>
          <BottomNav />
        </div>
      );
    }

    // ---------- Review Detail Landing (choose action) ----------

    const isAssignedManager =
      reviewData.manager_id != null && reviewData.manager_id === currentUser?.employee_id;
    const isOwnLandingReview = reviewData.employee_id === currentUser?.employee_id;
    // Peer identities are only ever shown to the assigned manager or an
    // admin, and never to the review's subject — matching the server rules.
    const canSeeLandingPeers = !isOwnLandingReview && (isAssignedManager || isAdmin);

    // The person being appraised gets their own landing: what's happening now,
    // the one thing to do, and the steps with dates.
    if (isOwnLandingReview && !adminUnlocked) {
      const now = appraisalNow(reviewData.status, reviewData.manager_name);
      const selfStage = isSelfAssessmentEditable;
      return (
        <div className="flex min-h-dvh flex-col bg-pme-paper safe-x">
          <header className="safe-top px-5 pt-4 pb-3 pr-28">
            <button type="button" onClick={goToList} className="text-sm text-pme-blue mb-2">&larr; Performance</button>
            <p className="eyebrow">{reviewData.cycle_name}</p>
            <h1 className="font-display text-3xl text-pme-navy">Your appraisal</h1>
            <p className="mt-0.5 text-xs text-gray-500">
              {reviewData.manager_name && <>With {reviewData.manager_name}</>}
              {reviewData.manager_name && reviewData.review_period_start && " · "}
              {reviewData.review_period_start && reviewData.review_period_end &&
                `${formatDate(reviewData.review_period_start)} – ${formatDate(reviewData.review_period_end)}`}
            </p>
          </header>

          <main className="mx-auto w-full max-w-2xl flex-1 overflow-y-auto px-5 py-4 pb-24 space-y-4">
            {justSubmitted && (
              <div className="rounded-xl border border-green-200 bg-green-50 p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-green-800">
                  <CheckCircle2 size={18} strokeWidth={2} aria-hidden />
                  Sent to {reviewData.manager_name || "your manager"}. Thank you.
                </p>
                <p className="mt-1 text-sm text-green-700">
                  You'll get an email when it's time for the next step. You can read what you wrote below at any time.
                </p>
              </div>
            )}

            <div className={`card p-4 ${selfStage ? "ring-1 ring-pme-blue/30" : ""}`}>
              {now.title && <p className="text-sm font-semibold text-pme-navy">{now.title}</p>}
              <p className="mt-1 text-sm text-gray-600">{now.body}</p>
              {selfStage && reviewData.self_assessment_deadline && (
                <p className="mt-2 text-xs font-semibold text-pme-navy">
                  Please finish by {formatDate(reviewData.self_assessment_deadline, "long")} · {dueText(reviewData.self_assessment_deadline)}
                </p>
              )}
              {selfStage && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchParams(
                      { review: String(selectedReviewId), tab: "self-assessment", step: String(resumeStep) },
                      { replace: true },
                    );
                  }}
                  className="mt-3 w-full rounded-xl bg-pme-blue py-3 text-sm font-bold text-white shadow hover:bg-pme-blue/90"
                >
                  {reviewData.status === "not_started"
                    ? "Start my self-assessment"
                    : completedSteps.size === 0
                      ? "Continue my self-assessment"
                      : `Continue my self-assessment (step ${resumeStep} of ${TOTAL_STEPS})`}
                </button>
              )}
            </div>

            {reviewData.meeting && reviewData.status !== "completed" && selectedReviewId && (
              <MyMeetingCard reviewId={selectedReviewId} meeting={reviewData.meeting} managerName={reviewData.manager_name} />
            )}

            <div className="card p-4">
              <p className="eyebrow mb-3">The steps</p>
              <AppraisalJourney
                status={reviewData.status}
                managerName={reviewData.manager_name}
                dates={{
                  self: reviewData.self_assessment_deadline,
                  peers: reviewData.colleague_feedback_deadline,
                  manager: reviewData.manager_review_deadline,
                }}
              />
            </div>

            {!selfStage && reviewData.overall_self_rating != null && (
              <div className="card p-4">
                <p className="eyebrow mb-1">What you submitted</p>
                <p className="text-sm text-gray-700">{reviewData.rating_words ? "Your view of your year" : "Your overall rating"}: <span className="font-semibold">{selfRatingText(reviewData.overall_self_rating, reviewData.rating_words)}</span></p>
                {(reviewData.top_priority_1 || reviewData.top_priority_2) && (
                  <ul className="mt-2 space-y-0.5 text-sm text-gray-600">
                    {reviewData.top_priority_1 && <li>Priority 1: {reviewData.top_priority_1}</li>}
                    {reviewData.top_priority_2 && <li>Priority 2: {reviewData.top_priority_2}</li>}
                  </ul>
                )}
                <p className="mt-2 text-xs text-gray-500">
                  {reviewData.manager_name || "Your manager"}'s ratings and comments are shown to you once the appraisal is complete.
                </p>
              </div>
            )}
          </main>
          <BottomNav />
        </div>
      );
    }

    const landingDeadlines: { label: string; date: string | null; stage: string }[] = [
      { label: "Self-assessment", date: reviewData.self_assessment_deadline, stage: "self" },
      { label: "Colleague feedback", date: reviewData.colleague_feedback_deadline, stage: "peers" },
      { label: "Manager review & meeting", date: reviewData.manager_review_deadline, stage: "manager" },
    ];
    const activeStage =
      ["not_started", "self_assessment_in_progress"].includes(reviewData.status) ? "self"
      : ["self_assessment_submitted", "colleague_feedback_in_progress"].includes(reviewData.status) ? "peers"
      : reviewData.status === "completed" ? "none" : "manager";

    return (
      <div className="flex min-h-dvh flex-col bg-pme-paper safe-x">
        <header className="safe-top px-5 pt-4 pb-3 pr-28">
          <button type="button" onClick={goToList} className="text-sm text-pme-blue mb-2">&larr; {fromEmployee ? "Back to Employee" : "Back to appraisals"}</button>
          <p className="eyebrow">{appraisalName(reviewData.appraisal_year)}{reviewData.cycle_name ? ` · ${reviewData.cycle_name}` : ""}</p>
          <h1 className="font-display text-3xl text-pme-navy">
            {isOwnLandingReview ? `Your ${appraisalName(reviewData.appraisal_year, false).toLowerCase()}` : reviewData.employee_name}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <StatusBadge status={reviewData.status} />
            {reviewData.manager_name && <span>Manager: {reviewData.manager_name}</span>}
            {reviewData.review_period_start && reviewData.review_period_end && (
              <span>
                {DATE_FMT.format(new Date(reviewData.review_period_start))} → {DATE_FMT.format(new Date(reviewData.review_period_end))}
              </span>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto px-5 py-4 pb-24 space-y-4">
          {/* Where this review is up to */}
          <div className="card p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Where this review is</h3>
            <div className="mt-2">
              <ReviewStagePips status={reviewData.status} size="md" />
            </div>
            <p className="mt-2 text-sm text-gray-600">{reviewStatusNext(reviewData.status)}</p>
          </div>

          {/* After the manager review: book and hold the review meeting */}
          {!isOwnLandingReview && (isAssignedManager || isAdmin) && isMeetingStage(reviewData.status) && selectedReviewId && (
            <MeetingPanel
              reviewId={selectedReviewId}
              employeeName={reviewData.employee_name}
              status={reviewData.status}
              meeting={reviewData.meeting}
              autoOpen={searchParams.get("book") === "1"}
              onChanged={(m) => setReviewData((prev) => (prev ? { ...prev, meeting: m } : prev))}
            />
          )}

          {!isOwnLandingReview && (isAssignedManager || isAdmin) && isMeetingStage(reviewData.status) && reviewData.status !== "employee_response_pending" && (
            <button
              type="button"
              onClick={() => setSearchParams({ review: String(selectedReviewId), tab: "view" }, { replace: true })}
              className="w-full rounded-xl border border-gray-200 bg-white py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Read the full review
            </button>
          )}

          {/* Colleague feedback progress — manager/admin only */}
          {canSeeLandingPeers && (
            <div className="card p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Colleague feedback</h3>
              {landingPeersError ? (
                <p className="mt-1 text-xs text-gray-400">{landingPeersError}</p>
              ) : landingPeers == null ? (
                <p className="mt-1 text-xs text-gray-400">Loading…</p>
              ) : (
                <>
                  {landingPeers.length > 0 && (
                    <p className="mt-1 text-xs text-gray-500">
                      {landingPeers.filter((p) => p.status === "submitted").length} of {landingPeers.length} submitted
                    </p>
                  )}
                  <PeerStatusChips peers={landingPeers} canResend={!["completed", "cancelled"].includes(reviewData.status)} emptyText="No colleagues chosen yet — pick them from My Team or Performance → Team Reviews." />
                </>
              )}
            </div>
          )}

          {/* Key dates */}
          <div className="card p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Key dates</h3>
            <div className="mt-2 space-y-1 text-sm">
              {landingDeadlines.filter((d) => d.date).map((d) => (
                <p key={d.label} className={d.stage === activeStage ? "font-semibold text-pme-navy" : "text-gray-500"}>
                  {d.label}: {DATE_FMT.format(new Date(d.date as string))}
                  {d.stage === activeStage && <span className="ml-1.5 rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-pme-blue">current stage</span>}
                </p>
              ))}
              {landingDeadlines.every((d) => !d.date) && (
                <p className="text-gray-400">No deadlines set.</p>
              )}
            </div>
          </div>

          {/* Ratings — only the ones that exist */}
          {(reviewData.overall_self_rating || reviewData.overall_manager_rating || reviewData.overall_agreed_rating) && (
            <div className="grid grid-cols-3 gap-2">
              {reviewData.overall_self_rating != null && (
                <div className="card p-3 text-center">
                  <p className={`font-bold text-pme-blue ${reviewData.rating_words ? "text-base leading-8" : "text-2xl"}`}>{reviewData.rating_words ? selfRatingText(reviewData.overall_self_rating, true) : reviewData.overall_self_rating}</p>
                  <p className="text-xs text-gray-500">{reviewData.rating_words ? "Their own view" : "Self rating"}</p>
                </div>
              )}
              {reviewData.overall_manager_rating != null && (
                <div className="card p-3 text-center">
                  <p className="text-2xl font-bold text-purple-600">{reviewData.overall_manager_rating}</p>
                  <p className="text-xs text-gray-500">Manager rating</p>
                </div>
              )}
              {reviewData.overall_agreed_rating != null && (
                <div className="card p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{reviewData.overall_agreed_rating}</p>
                  <p className="text-xs text-gray-500">Agreed rating</p>
                </div>
              )}
            </div>
          )}

          {/* Action buttons */}
          {isSelfAssessmentEditable && (
            <button
              type="button"
              onClick={() => {
                setSearchParams({ review: String(selectedReviewId), tab: "self-assessment", step: "1" }, { replace: true });
              }}
              className="w-full rounded-xl bg-pme-blue py-3 text-sm font-bold text-white hover:bg-pme-blue/90"
            >
              {reviewData.status === "not_started" ? "Start Self-Assessment" : "Continue Self-Assessment"}
            </button>
          )}

          {isManagerReviewEditable && (
            <button
              type="button"
              onClick={() => {
                setSearchParams({ review: String(selectedReviewId), tab: "manager-review", step: "1" }, { replace: true });
              }}
              className="w-full rounded-xl bg-purple-600 py-3 text-sm font-bold text-white hover:bg-purple-700"
            >
              {reviewData.status === "manager_review_in_progress" ? "Continue Manager Review" : "Start Manager Review"}
            </button>
          )}

          {reviewData.status === "self_assessment_submitted" && (
            <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-4 text-center">
              <p className="text-sm font-medium text-yellow-800">Self-assessment submitted</p>
              <p className="text-xs text-yellow-600 mt-1">Waiting for colleague feedback and manager review.</p>
            </div>
          )}
        </main>
        <BottomNav />
      </div>
    );
  }

  // =========================================================================
  // RENDER: List Mode
  // =========================================================================

  const handleListTabChange = (tab: "my-reviews" | "team-reviews") => {
    setListTab(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("list", tab);
      return next;
    }, { replace: true });
  };

  const skipColleagueFeedback = async (reviewId: number) => {
    try {
      await api.post(`/api/reviews/${reviewId}/skip-colleague-feedback`);
      void loadList();
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    }
  };

  return (
    <div className="flex min-h-dvh flex-col bg-white safe-x">
      <header className="safe-top px-5 pt-4 pb-3">
        <h1 className="text-2xl font-semibold text-gray-900">Appraisals</h1>
      </header>

      {/* Tabs */}
      <nav className="flex border-b border-gray-200 px-2 text-sm">
        <button
          type="button"
          onClick={() => handleListTabChange("my-reviews")}
          className={`whitespace-nowrap px-3 py-2 text-sm font-semibold ${
            listTab === "my-reviews" ? "border-b-2 border-pme-blue text-pme-blue" : "text-gray-500"
          }`}
        >
          My Reviews
        </button>
        {showTeamTab && (
          <button
            type="button"
            onClick={() => handleListTabChange("team-reviews")}
            className={`whitespace-nowrap px-3 py-2 text-sm font-semibold ${
              listTab === "team-reviews" ? "border-b-2 border-pme-blue text-pme-blue" : "text-gray-500"
            }`}
          >
            Team Reviews
          </button>
        )}
      </nav>

      <main className="flex-1 overflow-y-auto px-5 py-4 pb-24 space-y-4">
        {listLoading && <p className="text-sm text-gray-400">Loading reviews...</p>}
        {listError && <p className="text-sm text-red-600">{listError}</p>}

        {/* ── My Reviews Tab ── */}
        {listTab === "my-reviews" && !listLoading && (
          <>
            {/* Colleague feedback requests */}
            {colleagueFeedbacks.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-gray-700 mb-2">Pending Colleague Feedback</h2>
                {colleagueFeedbacks.map((fb) => (
                  <button
                    key={fb.id}
                    type="button"
                    onClick={() => goToFeedback(fb.id)}
                    className="w-full text-left mb-2 rounded-xl border border-orange-200 bg-orange-50 p-4 shadow-sm hover:bg-orange-100 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-gray-900">Feedback for {fb.employee_name}</p>
                      <StatusBadge status={fb.status} />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Focus: {fb.focus_area}</p>
                    {fb.deadline && (
                      <p className="text-xs text-gray-400">Due: {DATE_FMT.format(new Date(fb.deadline))}</p>
                    )}
                  </button>
                ))}
              </div>
            )}

            {reviews.length === 0 && colleagueFeedbacks.length === 0 && (
              <div className="text-center py-8">
                <p className="text-sm text-gray-400">No reviews found.</p>
              </div>
            )}

            {reviews.map((review) => (
              <button
                key={review.id}
                type="button"
                onClick={() => goToReview(review.id)}
                className="w-full text-left rounded-xl border border-gray-200 bg-white p-4 shadow-sm hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900">{review.employee_name || review.cycle_name}</p>
                    <p className="text-xs text-gray-500">{review.cycle_name}</p>
                  </div>
                  <StatusBadge status={review.status} />
                </div>

                <div className="mt-2 space-y-0.5">
                  {review.self_assessment_deadline && (
                    <p className="text-xs text-gray-500">Self-Assessment Due: {DATE_FMT.format(new Date(review.self_assessment_deadline))}</p>
                  )}
                  {review.manager_review_deadline && (
                    <p className="text-xs text-gray-500">Manager Review Due: {DATE_FMT.format(new Date(review.manager_review_deadline))}</p>
                  )}
                </div>

                {(review.overall_self_rating || review.overall_manager_rating || review.overall_agreed_rating) && (
                  <div className="flex gap-3 mt-2 text-xs">
                    {review.overall_self_rating && (
                      <span className="text-pme-blue font-medium">Self: {selfRatingText(review.overall_self_rating, review.rating_words)}</span>
                    )}
                    {review.overall_manager_rating && (
                      <span className="text-purple-600 font-medium">Manager: {review.overall_manager_rating}/5</span>
                    )}
                    {review.overall_agreed_rating && (
                      <span className="text-green-600 font-medium">Agreed: {review.overall_agreed_rating}/5</span>
                    )}
                  </div>
                )}

                {review.manager_name && (
                  <p className="text-xs text-gray-400 mt-1">Manager: {review.manager_name}</p>
                )}
              </button>
            ))}
          </>
        )}

        {/* ── Team Reviews Tab ── */}
        {listTab === "team-reviews" && showTeamTab && !listLoading && (
          <>
            {teamReviews.length === 0 && (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">No team reviews requiring action.</p>
              </div>
            )}

            {teamReviews.map((r) => {
              const canManageColleagues = r.status === "self_assessment_submitted" || r.status === "colleague_feedback_in_progress";
              const canStartManager = r.status === "manager_review_in_progress" || r.status === "colleague_feedback_complete";
              return (
                <div key={r.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900">{r.employee_name}</p>
                      <p className="text-xs text-gray-500">{r.cycle_name}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {r.manager_review_deadline && (
                        <span className="text-[10px] text-gray-400">Due: {DATE_FMT.format(new Date(r.manager_review_deadline))}</span>
                      )}
                      <StatusBadge status={r.status} />
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {canManageColleagues && (
                      <button
                        type="button"
                        onClick={() => setColleagueModalReview(r)}
                        className="rounded border border-blue-300 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
                      >
                        Select Colleagues
                      </button>
                    )}
                    {canManageColleagues && (
                      <button
                        type="button"
                        onClick={() => void skipColleagueFeedback(r.id)}
                        className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
                      >
                        Skip Colleague Feedback
                      </button>
                    )}
                    {canStartManager && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearchParams({ review: String(r.id), tab: "manager-review", step: "1" }, { replace: true });
                        }}
                        className="rounded border border-green-300 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50"
                      >
                        {r.status === "manager_review_in_progress" ? "Continue Manager Review" : "Start Manager Review"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </main>
      <BottomNav />

      {/* Colleague Selection Modal */}
      {colleagueModalReview && (
        <ReviewColleagueModal
          review={colleagueModalReview}
          onClose={() => { setColleagueModalReview(null); void loadList(); }}
        />
      )}
    </div>
  );
}

/* ── Colleague Selection Modal (for Team Reviews tab) ─────────────────────── */

function ReviewColleagueModal({ review, onClose }: { review: ReviewSummary; onClose: () => void }) {
  const [colleagues, setColleagues] = useState<{ id: number; colleague_id: number; colleague_name: string; focus_area: string; status: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);

  const loadColleagues = useCallback(async () => {
    try {
      setColleagues(await api.get(`/api/reviews/${review.id}/colleagues`));
    } catch { /* ignore */ }
    setLoading(false);
  }, [review.id]);

  useEffect(() => { void loadColleagues(); }, [loadColleagues]);

  const addColleague = async (id: number | null) => {
    if (!id) return;
    setAdding(true);
    try {
      await api.post(`/api/reviews/${review.id}/colleagues`, { colleague_id: id, focus_area: "general" });
      await loadColleagues();
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    }
    setAdding(false);
  };

  const removeColleague = async (feedbackId: number) => {
    setRemovingId(feedbackId);
    try {
      await api.delete(`/api/reviews/colleagues/${feedbackId}`);
      setColleagues((prev) => prev.filter((c) => c.id !== feedbackId));
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    }
    setRemovingId(null);
  };

  const submitted = colleagues.filter((c) => c.status === "submitted").length;
  const total = colleagues.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          Select Colleagues for {review.employee_name}
        </h2>
        <p className="text-xs text-gray-500 mb-4">Choose 3–5 colleagues who can give feedback.</p>

        {loading ? (
          <p className="text-sm text-gray-400">Loading...</p>
        ) : (
          <>
            {total > 0 && (
              <p className="text-xs font-medium text-gray-600 mb-2">
                {submitted}/{total} submitted
              </p>
            )}
            <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
              {colleagues.map((col) => (
                <div key={col.id} className="flex items-center gap-2 rounded border border-gray-100 bg-gray-50 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <span className="text-sm font-medium text-gray-800">{col.colleague_name}</span>
                    <span className={`ml-2 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${col.status === "submitted" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
                      {col.status}
                    </span>
                  </div>
                  {col.status !== "submitted" && (
                    <button
                      type="button"
                      onClick={() => void removeColleague(col.id)}
                      disabled={removingId === col.id}
                      className="shrink-0 rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      {removingId === col.id ? "..." : "Remove"}
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="mb-4">
              <EmployeeSearch
                value={null}
                onChange={(id) => void addColleague(id)}
                placeholder="Search for a colleague to add..."
                className="input text-sm"
                disabled={adding}
                excludeIds={[review.employee_id, ...colleagues.map((c) => c.colleague_id)]}
              />
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
